/**
 * Clara 360 - Mieteingangskontrolle
 * Month-based rent payment tracking with aggregation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { jsPDF } from 'jspdf';
import { 
  CheckCircle, XCircle, AlertCircle, AlertTriangle, Clock, Euro, Calendar, 
  ChevronLeft, ChevronRight, Upload, Download, RefreshCw,
  TrendingUp, TrendingDown, DollarSign, Users, Building2,
  MessageSquare, Mail, FileText, ArrowUpDown, ChevronDown, ChevronUp, WalletCards
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import rentPaymentsService from '@/services/rentPaymentsService';
import propertiesService from '@/services/propertiesService';
import RentCsvUpload from './RentCsvUpload';
import CsvMatchReview from './CsvMatchReview';
import RentPaymentCommunicationModal from './RentPaymentCommunicationModal';
import NkMahnNotice from './NkMahnNotice';
import TransactionReviewQueue from '@/components/TransactionReviewQueue';
import * as XLSX from 'xlsx';
import PaymentTrafficModal from './PaymentTrafficModal';
import { masterSupabase } from '../../lib/supabase-master.js';

const ledgerCurrency = (amount) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(amount || 0));
const ledgerTone = (state) => ({ paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', overpaid: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200', tenant_review: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200', partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', overdue: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200', unknown: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300', bankeingang_soll_offen: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200', soll_zeitraum_offen: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200' }[state] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200');
const ledgerStateLabel = (state) => ({ paid: 'Bezahlt', overpaid: 'Bezahlt · Mehrbetrag', tenant_review: 'Mieter-Review erforderlich', partial: 'Teilzahlung', overdue: 'Offen', pending: 'Noch nicht fällig', unknown: 'Zahlungsstatus unbekannt', bankeingang_soll_offen: 'Bankeingang · Soll offen', soll_zeitraum_offen: 'Soll-/Zeitraum offen' }[state] || 'Prüfung offen');
// Der Warmmieten-Sollwert bleibt für Soll/Ist immer führend. Die Komponenten
// dienen ausschließlich der Transparenz und werden nie aus einer Differenz geschätzt.
const rentComponentLabel = (coldRent, utilitiesAdvance, warmRent) => {
  const cold = coldRent === null || coldRent === undefined ? null : Number(coldRent);
  const utilities = utilitiesAdvance === null || utilitiesAdvance === undefined ? null : Number(utilitiesAdvance);
  const warm = warmRent === null || warmRent === undefined ? null : Number(warmRent);
  if (cold !== null && utilities !== null && warm !== null) return `Nettomiete ${ledgerCurrency(cold)} + NK-Vorauszahlung ${ledgerCurrency(utilities)} = Warmmiete ${ledgerCurrency(warm)}`;
  if (cold !== null && warm !== null) return `Nettomiete ${ledgerCurrency(cold)} · NK-Vorauszahlung nicht separat belegt · Warmmiete ${ledgerCurrency(warm)}`;
  return null;
};

const rentWorkingComparisonPresentation = (payment) => {
  const outcome = payment?.rent_receipt_comparison;
  const delta = payment?.rent_receipt_comparison_delta;
  const deltaLabel = delta === null || delta === undefined ? '' : ` · Abweichung ${delta > 0 ? '+' : ''}${ledgerCurrency(delta)}`;
  const presentations = {
    confirmed_receipt_matches_current_working_value: {
      label: 'Mietbeleg entspricht Arbeitswert',
      tone: 'text-emerald-700 dark:text-emerald-300',
    },
    confirmed_receipt_differs_from_current_working_value: {
      label: `Mietbeleg weicht ab${deltaLabel}`,
      tone: 'text-amber-700 dark:text-amber-300',
    },
    no_confirmed_rent_payment_available: {
      label: 'Noch kein bestätigter Mietbeleg',
      tone: 'text-slate-500 dark:text-slate-400',
    },
    comparison_blocked_multiple_confirmed_receipts: {
      label: 'Mehrere Mietbelege prüfen',
      tone: 'text-amber-700 dark:text-amber-300',
    },
    comparison_blocked_bulk_transfer: {
      label: 'Sammelzahlung prüfen',
      tone: 'text-amber-700 dark:text-amber-300',
    },
    comparison_blocked_unverified_rent: {
      label: 'Sollwert-Quelle prüfen',
      tone: 'text-rose-700 dark:text-rose-300',
    },
    comparison_blocked_baseline_drift: {
      label: 'Arbeitswert-Drift · Vergleich gesperrt',
      tone: 'text-rose-700 dark:text-rose-300',
    },
  };
  const presentation = presentations[outcome];
  return presentation ? { ...presentation, note: payment?.rent_receipt_comparison_note || '' } : null;
};

const ledgerExportFilePart = (value) => String(value || 'mieter').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'mieter';
const ledgerSheet = (rows, widths = []) => {
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  if (widths.length) sheet['!cols'] = widths.map(width => ({ wch: width }));
  return sheet;
};
const exportTenantLedgerWorkbook = (ledger, payment) => {
  if (!ledger) return;
  const summary = ledger.summary || {};
  const months = ledger.months || [];
  const exceptions = ledger.exception_payments || [];
  const tenantName = ledger.tenant?.name || payment?.tenant_name || 'Mieter';
  const propertyName = ledger.property?.name || payment?.property_name || 'Objekt';
  const unitName = ledger.unit?.identifier || payment?.unit_number || 'Einheit';
  const year = ledger.year || new Date().getFullYear();
  const workbook = XLSX.utils.book_new();

  const uiOverviewRows = [
    ['Mietübersicht – interne Prüfunterlage'],
    ['Mieter', tenantName, 'Objekt', propertyName, 'Einheit', unitName],
    ['Mietjahr', year, 'Belegter Soll', Number(summary.expected_eur || 0), 'Miet-Bankeingänge', Number(summary.received_eur || 0)],
    ['Offen bei belegtem Soll', Number(summary.open_eur || 0), 'Monate mit Zeitraum-Evidence', String(summary.months_with_period_evidence || 0) + '/12', '', ''],
    [],
    ['Monat', 'Soll', 'Bezahlt', 'Offen', 'Mehrbetrag', 'Status', 'Zahlung / Hinweis']
  ].concat(months.map(month => [
    month.month_label,
    month.expected_eur == null ? 'Soll offen' : Number(month.expected_eur),
    Number(month.received_eur || 0),
    month.outstanding_eur == null ? '–' : Number(month.outstanding_eur),
    Number(month.surplus_eur || 0),
    ledgerStateLabel(month.state),
    (month.payment_details || []).length > 0
      ? (month.payment_details || []).map(entry => (entry.booking_date || '') + ' · ' + ledgerCurrency(entry.amount_eur) + ' · ' + (entry.evidence_label || 'Bankeingang')).join(' | ')
      : (month.period_evidence_label || '')
  ]));
  const uiOverviewSheet = ledgerSheet(uiOverviewRows, [20, 16, 18, 16, 18, 27, 92]);
  uiOverviewSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
  uiOverviewSheet['!autofilter'] = { ref: 'A6:G' + uiOverviewRows.length };
  XLSX.utils.book_append_sheet(workbook, uiOverviewSheet, 'Mietübersicht');

  const evidenceRows = [
    ['Monat', 'Buchungstag', 'Betrag', 'Evidence-Label', 'Verwendungszweck / Auszug']
  ];
  months.forEach(month => (month.payment_details || []).forEach(entry => evidenceRows.push([
    month.month_label,
    entry.booking_date || '',
    Number(entry.amount_eur || 0),
    entry.evidence_label || '',
    entry.reference_excerpt || 'ohne Referenz'
  ])));
  if (evidenceRows.length === 1) evidenceRows.push(['Keine berücksichtigten Bankeingänge im Ledger', '', '', '', '']);
  const evidenceSheet = ledgerSheet(evidenceRows, [18, 16, 16, 34, 82]);
  evidenceSheet['!autofilter'] = { ref: 'A1:E' + evidenceRows.length };
  XLSX.utils.book_append_sheet(workbook, evidenceSheet, 'Bankevidence');

  const exceptionRows = [
    ['Buchungstag', 'Betrag', 'Einordnung', 'Hinweis']
  ].concat(exceptions.map(entry => [
    entry.booking_date || '',
    Number(entry.amount_eur || 0),
    entry.evidence_label || '',
    entry.reference_excerpt || entry.evidence_note || ''
  ]));
  if (exceptionRows.length === 1) exceptionRows.push(['Keine Kautionen oder Sonderzahlungen im Ledger', '', '', '']);
  const exceptionSheet = ledgerSheet(exceptionRows, [18, 16, 32, 88]);
  exceptionSheet['!autofilter'] = { ref: 'A1:D' + exceptionRows.length };
  XLSX.utils.book_append_sheet(workbook, exceptionSheet, 'Ausgeschlossen');

  const gateRows = [
    ['Monat', 'Status', 'Forderung als belegt ausweisbar?', 'Begründung']
  ].concat(months.map(month => [
    month.month_label,
    ledgerStateLabel(month.state),
    month.expected_eur != null && ['overdue', 'partial'].includes(month.state) ? 'Ja, nur interne Prüfung' : 'Nein / nicht als Forderung verwenden',
    month.expected_eur == null ? 'Soll oder Mieterzeitraum nicht vollständig belegt' : (month.period_evidence_label || 'Evidence prüfen')
  ]));
  const gateSheet = ledgerSheet(gateRows, [18, 28, 36, 78]);
  gateSheet['!autofilter'] = { ref: 'A1:D' + gateRows.length };
  XLSX.utils.book_append_sheet(workbook, gateSheet, 'Hinweise & Gates');

  XLSX.writeFile(workbook, 'Mietverlauf_' + ledgerExportFilePart(tenantName) + '_' + String(year) + '.xlsx');
};
function TenantLedgerDetailView({ ledger, payment, loading, hasError, onBack, onRetry, onSelectYear, onConfirmPeriod, onOpenArrearsCommunication, initialArrearsPreview = false, onCloseArrearsPreview }) {
  const summary = ledger?.summary || {};
  const months = ledger?.months || [];
  const exceptions = ledger?.exception_payments || [];
  const title = ledger?.tenant?.name || payment?.tenant_name || 'Mieter';
  const propertyName = ledger?.property?.name || payment?.property_name || 'Objekt';
  const unitName = ledger?.unit?.identifier || payment?.unit_number || 'Einheit';
  const year = ledger?.year || payment?.year || new Date().getFullYear();
  const availableYears = (() => {
    const nowDate = new Date();
    const nowYear = nowDate.getFullYear();
    const ruleYears = [];
    for (let y = 2025; y <= nowYear; y += 1) ruleYears.push(y);
    if (nowDate.getMonth() === 11) ruleYears.push(nowYear + 1);
    const ledgerYears = Array.isArray(ledger?.available_years) ? ledger.available_years : [];
    const fallback = Number(year);
    return [...new Set([...ledgerYears, ...ruleYears, fallback])]
      .filter((y) => Number.isInteger(y) && y >= 2000 && y <= 2100)
      .sort((a, b) => b - a);
  })();
  const [confirmingEntryId, setConfirmingEntryId] = useState(null);
  const [confirmError, setConfirmError] = useState(null);
  const [targetPeriodByEntry, setTargetPeriodByEntry] = useState({});
  const allMonthsForTarget = ledger?.months || [];
  const openTargetMonths = allMonthsForTarget.filter((m) => m.expected_eur != null);
  const confirmEntryPeriod = async (entry, defaultMonthKey) => {
    const targetPeriod = targetPeriodByEntry[entry.transaction_id] || entry.matched_period || defaultMonthKey;
    if (!targetPeriod) { setConfirmError('Bitte einen Zielmonat auswählen.'); return; }
    const targetExpected = Number((allMonthsForTarget.find((m) => m.month_key === targetPeriod) || {}).expected_eur || 0);
    const confirmationType = Number(entry.amount_eur || 0) >= targetExpected - 0.01 ? 'full_payment' : 'partial_payment';
    setConfirmingEntryId(entry.transaction_id);
    setConfirmError(null);
    try {
      await onConfirmPeriod?.({ transactionId: entry.transaction_id, targetPeriod, confirmationType });
    } catch (error) {
      setConfirmError(String(error.message || error));
    } finally {
      setConfirmingEntryId(null);
    }
  };
  const [showArrearsPreview, setShowArrearsPreview] = useState(initialArrearsPreview);
  const [selectedMonthKeys, setSelectedMonthKeys] = useState(() => initialArrearsPreview ? months.filter(month => month.expected_eur != null && ['overdue', 'partial'].includes(month.state)).map(month => month.month_key) : []);
  const eligibleMonths = months.filter(month => month.expected_eur != null && ['overdue', 'partial'].includes(month.state));
  const eligibleMonthSignature = eligibleMonths.map(month => month.month_key).join('|');
  useEffect(() => {
    if (initialArrearsPreview) {
      setShowArrearsPreview(true);
      setSelectedMonthKeys(eligibleMonths.map(month => month.month_key));
      runDunningClauseCheck();
    }
  }, [initialArrearsPreview, eligibleMonthSignature]);
  const excludedMonths = months.filter(month => !eligibleMonths.some(eligible => eligible.month_key === month.month_key));
  const selectedMonths = eligibleMonths.filter(month => selectedMonthKeys.includes(month.month_key));
  const selectedOpenEur = selectedMonths.reduce((sum, month) => sum + Number(month.outstanding_eur || 0), 0);
  const [dunningCheck, setDunningCheck] = useState({ state: 'idle', finding: null, error: null, confirming: false, confirmed: false });
  const [letterDraft, setLetterDraft] = useState(null);
  const [letterBusy, setLetterBusy] = useState(false);
  const [letterFiled, setLetterFiled] = useState(null);
  const [letterError, setLetterError] = useState(null);
  const [letterSalutationPrompt, setLetterSalutationPrompt] = useState(false);
  const [letterMahnkosten, setLetterMahnkosten] = useState('');
  const prepareLetterDraft = async (salutationChoice) => {
    if (selectedMonths.length === 0) return;
    const scope = { propertyId: ledger?.property?.id || payment?.property_id, unitId: ledger?.unit?.id || payment?.unit_id, tenantName: ledger?.tenant?.name || payment?.tenant_name };
    if (!scope.propertyId || !scope.unitId || !scope.tenantName) {
      setLetterError('Mieterdaten noch nicht geladen - bitte die Mietermaske oeffnen und erneut Brief erstellen klicken.');
      return;
    }
    setLetterBusy(true); setLetterError(null); setLetterFiled(null);
    try {
      const months = selectedMonths.map((month) => ({ label: month.month_label, expected: month.expected_eur, received: month.received_eur, outstanding: month.outstanding_eur }));
      const draft = await rentPaymentsService.createLetterDraft({
        propertyId: scope.propertyId,
        unitId: scope.unitId,
        tenantName: scope.tenantName,
        salutation: salutationChoice || ledger?.tenant?.salutation || payment?.salutation || '',
        mahnkosten: letterMahnkosten === '' ? null : Number(letterMahnkosten),
        year,
        months,
      });
      setLetterDraft(draft);
    } catch (error) {
      const message = String(error.message || error);
      if (message === 'salutation_required') {
        setLetterSalutationPrompt(true);
        setLetterError('Bitte Anrede fuer den Mieter waehlen (wird im Mieterstamm gespeichert):');
      } else {
        setLetterError(message);
      }
    } finally {
      setLetterBusy(false);
    }
  };
  const printLetterDraft = () => {
    if (!letterDraft?.pdf_base64) return;
    const bytes = atob(letterDraft.pdf_base64);
    const array = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i += 1) array[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([array], { type: 'application/pdf' }));
    const win = window.open(url, '_blank');
    if (win) { win.onload = () => { win.print(); }; }
  };
  const fileLetterDraft = async () => {
    if (!letterDraft?.pdf_base64) return;
    setLetterBusy(true); setLetterError(null);
    try {
      const filename = `Mahnung_${String(ledger?.tenant?.name || payment?.tenant_name || 'mieter').replace(/[^A-Za-z0-9äöüÄÖÜß-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      const result = await rentPaymentsService.fileLetter({
        propertyId: ledger?.property?.id || payment?.property_id,
        unitId: ledger?.unit?.id || payment?.unit_id,
        tenantName: ledger?.tenant?.name || payment?.tenant_name,
        filename,
        pdfBase64: letterDraft.pdf_base64,
      });
      setLetterFiled(result);
    } catch (error) {
      setLetterError(String(error.message || error));
    } finally {
      setLetterBusy(false);
    }
  };
  const runDunningClauseCheck = () => {
    const scope = {
      propertyId: ledger?.property?.id || payment?.property_id,
      unitId: ledger?.unit?.id || payment?.unit_id,
      tenantName: ledger?.tenant?.name || payment?.tenant_name,
    };
    if (!scope.propertyId || !scope.unitId || !scope.tenantName) return;
    setDunningCheck({ state: 'running', finding: null, error: null, confirming: false, confirmed: false });
    rentPaymentsService.checkDunningClause({ ...scope, year })
      .then((result) => setDunningCheck({ state: 'done', finding: result?.fee_finding || null, error: null, confirming: false, confirmed: false }))
      .catch((error) => setDunningCheck({ state: 'error', finding: null, error: String(error.message || error), confirming: false, confirmed: false }));
  };
  const confirmDunningFinding = async () => {
    const finding = dunningCheck.finding;
    if (!finding?.document_id || !finding.type) return;
    setDunningCheck((prev) => ({ ...prev, confirming: true }));
    try {
      await rentPaymentsService.confirmDunningClause({
        propertyId: ledger?.property?.id || payment?.property_id,
        unitId: ledger?.unit?.id || payment?.unit_id,
        tenantName: ledger?.tenant?.name || payment?.tenant_name,
        documentId: finding.document_id,
        findingType: finding.type,
        quote: finding.quote || '',
      });
      setDunningCheck((prev) => ({ ...prev, confirming: false, confirmed: true }));
    } catch (error) {
      setDunningCheck((prev) => ({ ...prev, confirming: false, error: String(error.message || error) }));
    }
  };
  const openArrearsPreview = () => {
    setSelectedMonthKeys(eligibleMonths.map(month => month.month_key));
    setShowArrearsPreview(true);
    runDunningClauseCheck();
  };
  const toggleArrearsMonth = (monthKey) => setSelectedMonthKeys(current => current.includes(monthKey) ? current.filter(key => key !== monthKey) : [...current, monthKey]);
  const buildArrearsSnapshot = () => ({
    snapshot_type: 'rent_arrears_selected_months_v1',
    generated_at: new Date().toISOString(),
    tenant: { id: ledger?.tenant?.id || payment?.tenant_id || '', name: title },
    property: { id: ledger?.property?.id || payment?.property_id || '', name: propertyName },
    unit: { id: ledger?.unit?.id || payment?.unit_id || '', identifier: unitName },
    year,
    months: selectedMonths.map(month => ({
      month_key: month.month_key,
      month_label: month.month_label,
      expected_eur: Number(month.expected_eur || 0),
      received_eur: Number(month.received_eur || 0),
      outstanding_eur: Number(month.outstanding_eur || 0),
      state: month.state,
      note: month.state === 'partial' ? 'Teilzahlung berücksichtigt' : 'Bitte Zahlungsstatus prüfen'
    })),
    total_open_eur: Number(selectedOpenEur || 0),
    test_anchor_transaction_id: payment?.is_test_data ? (payment?.test_anchor_transaction_id || payment?.id) : null,
  });
  const openArrearsCommunication = (channel) => {
    if (!selectedMonths.length) return;
    onOpenArrearsCommunication?.(buildArrearsSnapshot(), channel);
  };

  if (showArrearsPreview) return <div className="mx-auto max-w-5xl">
    <button type="button" onClick={() => initialArrearsPreview ? onCloseArrearsPreview?.() : setShowArrearsPreview(false)} className="mb-5 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-violet-700 dark:hover:bg-violet-950/30 dark:hover:text-violet-200"><ChevronLeft className="h-4 w-4" />Zurück zur Monatsprüfung</button>
    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-900/60 dark:bg-slate-900">
      <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 via-white to-indigo-50 px-5 py-5 dark:border-violet-900/60 dark:from-violet-950/40 dark:via-slate-900 dark:to-indigo-950/30 md:px-7"><div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"><div><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300"><FileText className="h-4 w-4" />Zahlungsübersicht für Mieter</div><h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">{title}</h1><p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{propertyName} · {unitName} · Mietjahr {year}</p></div><span className="inline-flex w-fit rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">Entwurf · kein Versand</span></div></div>
      <div className="p-5 md:p-7"><div className="rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-200">Diese Vorschau enthält nur Monate mit belegtem Sollbetrag, belegtem Mieterzeitraum und dem Status <strong>offen</strong> oder <strong>Teilzahlung</strong>. Sie erstellt keine Forderung und versendet keine Nachricht.</div>
        {eligibleMonths.length === 0 ? <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300">Für diesen Mietverlauf gibt es derzeit keine evidenzbasierten offenen oder teilbezahlten Monate für eine Mieterübersicht.</div> : <><div className="mt-5 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/40 sm:flex-row sm:items-center sm:justify-between"><div><div className="text-sm font-semibold text-gray-900 dark:text-white">{selectedMonths.length} von {eligibleMonths.length} Monaten ausgewählt</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Ausgewählte offene Summe</div></div><div className="flex items-center gap-3"><span className="text-xl font-bold text-rose-700 dark:text-rose-300">{ledgerCurrency(selectedOpenEur)}</span><Button onClick={() => setSelectedMonthKeys(selectedMonthKeys.length === eligibleMonths.length ? [] : eligibleMonths.map(month => month.month_key))} variant="outline" size="sm">{selectedMonthKeys.length === eligibleMonths.length ? 'Auswahl aufheben' : 'Alle auswählen'}</Button></div></div>
        <section className="mt-5 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700"><div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-950/60"><h2 className="text-sm font-semibold text-gray-900 dark:text-white">Vorschau der ausgewählten Monatsübersicht</h2><p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Die Tabelle wird später unverändert die Grundlage für PDF, E-Mail- und WhatsApp-Entwurf.</p></div><div className="overflow-x-auto"><table className="w-full min-w-[800px] text-sm"><thead className="bg-white text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-900 dark:text-gray-400"><tr><th className="w-12 px-4 py-3"></th><th className="px-4 py-3">Monat</th><th className="px-4 py-3 text-right">Soll</th><th className="px-4 py-3 text-right">Bereits bezahlt</th><th className="px-4 py-3 text-right">Noch offen</th><th className="px-4 py-3">Hinweis</th></tr></thead><tbody className="divide-y divide-slate-100 dark:divide-slate-800">{eligibleMonths.map(month => <tr key={month.month_key} className="align-top hover:bg-violet-50/40 dark:hover:bg-violet-950/15"><td className="px-4 py-3"><input aria-label={month.month_label + ' auswählen'} type="checkbox" checked={selectedMonthKeys.includes(month.month_key)} onChange={() => toggleArrearsMonth(month.month_key)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" /></td><td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{month.month_label}</td><td className="px-4 py-3 text-right">{ledgerCurrency(month.expected_eur)}</td><td className="px-4 py-3 text-right text-emerald-700 dark:text-emerald-300">{ledgerCurrency(month.received_eur)}</td><td className="px-4 py-3 text-right font-semibold text-rose-700 dark:text-rose-300">{ledgerCurrency(month.outstanding_eur)}</td><td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{month.state === 'partial' ? 'Teilzahlung berücksichtigt' : 'Bitte Zahlungsstatus prüfen'}</td></tr>)}</tbody></table></div></section>
      {(ledger?.dunning_clause?.present || dunningCheck.state !== 'idle') && (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/20">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-950 dark:text-amber-100"><AlertCircle className="h-4 w-4" />Mahnkosten &amp; Verzugszinsen laut Mietvertrag</h3>
          <div className="mt-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-900 dark:border-rose-800/60 dark:bg-rose-950/30 dark:text-rose-100">
            Liegt ein Mieter-Hinweis vor (Mietminderung, Stundung, Ratenplan), ist vor jeder Mahnung zu prüfen, ob die Minderung berechtigt ist. Keine automatische Sperre – bewusste Entscheidung erforderlich.
          </div>
          {ledger?.dunning_clause?.present && (
            <p className="mt-1 text-xs leading-5 text-amber-900 dark:text-amber-200">
              Der Mietvertrag enthält Klauseln zu <strong>Verzugszinsen</strong> und einer <strong>Mahngebühr je schriftlicher Mahnung</strong>.
              Diese Beträge sind <strong>nicht</strong> in „Noch offen“ enthalten und werden nicht automatisch berechnet, gefordert, gebucht oder kommuniziert.
              {ledger.dunning_clause.note}
            </p>
          )}
          {(ledger?.dunning_clause?.clauses || []).map((clause) => (
            <div key={clause.fact_key} className="mt-2 rounded-lg border border-amber-200 bg-white/80 px-3 py-2 text-xs text-amber-950 dark:border-amber-900/50 dark:bg-slate-950/40 dark:text-amber-100">
              <strong>{clause.label}:</strong> {clause.quote || 'Klauseltext nicht überliefert'}
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px]">
                {clause.human_verified && <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">Klausel geprüft</span>}
                <span className="rounded-full bg-amber-200 px-2 py-0.5 font-semibold text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">Betrag offen · nicht berechnet</span>
                <span className="text-amber-800 dark:text-amber-200">Rechtliche Anwendung im Einzelfall freigeben.</span>
              </div>
            </div>
          ))}
          {dunningCheck.state === 'running' && <div className="mt-2 text-xs text-amber-900 dark:text-amber-200">Automatischer Klausel-Check läuft … (reine Leseprüfung, nichts wird gespeichert)</div>}
          {dunningCheck.state === 'done' && dunningCheck.finding && dunningCheck.finding.type !== 'none' && (
            <div className="mt-2 rounded-lg border border-amber-300 bg-white/80 px-3 py-2 text-xs text-amber-950 dark:border-amber-900/50 dark:bg-slate-950/40 dark:text-amber-100">
              <strong>Automatischer Scan-Befund: {dunningCheck.finding.type === 'both' ? 'Klausel + fester Betrag' : dunningCheck.finding.type === 'fixed_amount' ? 'Fester Betrag' : 'Klausel'}</strong>
              <div className="mt-1">{dunningCheck.finding.quote ? String(dunningCheck.finding.quote).slice(0, 300) : 'Klauseltext nicht überliefert'}</div>
              {(dunningCheck.finding.amount_candidates || []).map((amount) => (
                <div key={amount.raw}>{amount.raw}{amount.eur != null ? ` ≈ ${Number(amount.eur).toFixed(2)} €` : ''}{amount.note ? ` – ${amount.note}` : ''}</div>
              ))}
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {dunningCheck.confirmed
                  ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">Klausel übernommen – geprüft</span>
                  : <Button onClick={confirmDunningFinding} disabled={dunningCheck.confirming} size="sm" className="bg-amber-600 text-white hover:bg-amber-700">{dunningCheck.confirming ? 'Übernahme …' : 'Klausel übernehmen'}</Button>}
                <span className="text-amber-800 dark:text-amber-200">Übernahme nur als geprüftes Faktum – ohne Forderungsbetrag.</span>
              </div>
            </div>
          )}
          {dunningCheck.state === 'done' && (!dunningCheck.finding || dunningCheck.finding.type === 'none') && (
            <div className="mt-2 text-xs text-amber-900 dark:text-amber-200">Keine Mahnkostenklausel in den erreichbaren Vertragsdokumenten erkannt.</div>
          )}
          {dunningCheck.state === 'error' && <div className="mt-2 text-xs text-amber-900 dark:text-amber-200">Klausel-Check nicht verfügbar – die Anzeige bleibt unverändert.</div>}
        </div>
      )}
      {letterDraft && (
        <div className="mt-5 rounded-xl border border-slate-300 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/30">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Mahnbrief (GRN-Briefkopf) – Entwurf vom {letterDraft.letter?.date}</h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Betreff: {letterDraft.letter?.subject}</p>
          <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-gray-800 dark:border-slate-700 dark:bg-slate-900 dark:text-gray-200">
            <strong>Zahlungsziel je Objekt:</strong>{' '}
            {letterDraft.account?.account
              ? <span>{letterDraft.account.account.holder || 'Kontoinhaber prüfen'} · {letterDraft.account.account.bank || ''}{letterDraft.account.account.badge ? ' · geteiltes Objektkonto' : ''}</span>
              : <span className="font-semibold text-rose-700 dark:text-rose-300">BITTE PRÜFEN – kein eindeutiges Objektkonto ({letterDraft.account?.reason || 'unbekannt'})</span>}
            <div className="mt-1 text-gray-600 dark:text-gray-300">Datum = Druckdatum ({letterDraft.letter?.date}). Nichts wird versendet; der Brief ist ein Entwurf bis Sie drucken und ablegen.</div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button onClick={printLetterDraft} size="sm" className="bg-slate-700 text-white hover:bg-slate-800">Brief drucken / als PDF speichern</Button>
            {letterFiled
              ? <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">Abgelegt in Capital, Vault, Paperless, Clara360</span>
              : <Button onClick={fileLetterDraft} disabled={letterBusy} size="sm" className="bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50">Brief ablegen</Button>}
            {letterError && <span className="text-xs text-rose-700 dark:text-rose-300">{letterError}</span>}
            {letterDraft?.mahnkosten_note && <span className="text-xs text-slate-600 dark:text-slate-300">{letterDraft.mahnkosten_note}</span>}
            {letterSalutationPrompt && (
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => prepareLetterDraft('Herr')} disabled={letterBusy} size="sm" className="bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">Sehr geehrter Herr</Button>
                <Button onClick={() => prepareLetterDraft('Frau')} disabled={letterBusy} size="sm" className="bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">Sehr geehrte Frau</Button>
                <Button onClick={() => prepareLetterDraft('Damen und Herren')} disabled={letterBusy} size="sm" className="bg-slate-700 text-white hover:bg-slate-800 disabled:opacity-50">Sehr geehrte Damen und Herren</Button>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-5 rounded-xl border border-violet-200 bg-violet-50/50 p-4 dark:border-violet-900/60 dark:bg-violet-950/20"><div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><div className="text-sm text-violet-800 dark:text-violet-200"><strong>Entwurf aus ausgewählten Monaten:</strong> Die gewählten Zeilen werden unverändert an die bestehende E-Mail- oder WhatsApp-Prüfoberfläche übergeben. Ein Versand bleibt bis zur jeweiligen finalen Freigabe gesperrt.</div><div className="flex shrink-0 flex-wrap gap-2"><Button disabled={selectedMonths.length === 0} onClick={() => openArrearsCommunication('email')} size="sm" className="bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"><Mail className="mr-2 h-4 w-4" />E-Mail vorbereiten</Button><Button disabled={selectedMonths.length === 0} onClick={() => openArrearsCommunication('whatsapp')} size="sm" className="bg-green-700 text-white hover:bg-green-800 disabled:opacity-50"><MessageSquare className="mr-2 h-4 w-4" />WhatsApp vorbereiten</Button><input type="number" min="0" step="0.01" value={letterMahnkosten} onChange={(e) => setLetterMahnkosten(e.target.value)} placeholder="Mahnkosten € (optional)" className="h-8 w-40 rounded-md border border-violet-300 bg-white px-2 text-sm text-slate-800" /><Button disabled={selectedMonths.length === 0 || letterBusy} onClick={() => prepareLetterDraft()} size="sm" className="bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">Brief erstellen</Button></div></div></div></>}</div>
    </section>
  </div>;

  return <div className="mx-auto max-w-7xl">
    <button type="button" onClick={onBack} className="mb-5 inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-violet-700 dark:hover:bg-violet-950/30 dark:hover:text-violet-200">
      <ChevronLeft className="h-4 w-4" />Zurück zur Mieteingangskontrolle
    </button>

    <section className="overflow-hidden rounded-2xl border border-violet-100 bg-white shadow-sm dark:border-violet-900/60 dark:bg-slate-900">
      <div className="border-b border-violet-100 bg-gradient-to-r from-violet-50 via-white to-indigo-50 px-5 py-5 dark:border-violet-900/60 dark:from-violet-950/40 dark:via-slate-900 dark:to-indigo-950/30 md:px-7">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-violet-700 dark:text-violet-300"><WalletCards className="h-4 w-4" />Vollständige Zahlungshistorie · Jahr wählen & Zahlungen bestätigen</div>
            <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">{title}</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{propertyName} · {unitName} · Mietjahr <select value={String(year)} onChange={(event) => onSelectYear?.(Number(event.target.value))} className="ml-1 rounded border border-violet-300 bg-white px-2 py-0.5 text-sm text-slate-800 focus:ring-violet-500 dark:border-violet-700 dark:bg-slate-900 dark:text-slate-100">{availableYears.map((yy) => <option key={yy} value={yy}>{yy}</option>)}</select></p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:items-end"><span className="inline-flex w-fit rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-violet-700 shadow-sm dark:bg-slate-950/70 dark:text-violet-200">Capital-Bankevidence</span>{ledger && <><Button onClick={openArrearsPreview} variant="default" size="sm" className="bg-violet-700 text-white shadow-sm hover:bg-violet-800 dark:bg-violet-600 dark:hover:bg-violet-500"><FileText className="mr-2 h-4 w-4" />Zahlungsübersicht erstellen</Button><Button onClick={() => exportTenantLedgerWorkbook(ledger, payment)} variant="outline" size="sm" className="border-violet-200 bg-white text-violet-800 shadow-sm hover:bg-violet-50 dark:border-violet-800 dark:bg-slate-950 dark:text-violet-200 dark:hover:bg-violet-950/60"><Download className="mr-2 h-4 w-4" />Mietverlauf exportieren</Button></>}</div>
        </div>
      </div>

      <div className="p-5 md:p-7">
        {loading && <div className="flex min-h-56 items-center justify-center rounded-xl border border-dashed border-violet-200 bg-violet-50/50 p-8 text-sm text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/20 dark:text-violet-200"><RefreshCw className="mr-2 h-5 w-5 animate-spin" />Mietchronik wird aus Capital geladen …</div>}
        {hasError && !loading && <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-200"><div className="font-semibold">Mietchronik konnte nicht geladen werden.</div><p className="mt-1">Die Übersicht wurde nicht verändert. Bitte versuchen Sie den read-only Abruf erneut.</p><Button onClick={onRetry} variant="outline" size="sm" className="mt-3 border-rose-300 bg-white text-rose-700 hover:bg-rose-100 dark:border-rose-800 dark:bg-slate-900 dark:text-rose-200">Erneut laden</Button></div>}
        {ledger && !loading && <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40"><div className="text-2xl font-semibold text-gray-900 dark:text-white">{ledgerCurrency(summary.expected_eur)}</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Belegter Sollbetrag · Warmmiete</div></div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20"><div className="text-2xl font-semibold text-emerald-700 dark:text-emerald-300">{ledgerCurrency(summary.received_eur)}</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Miet-Bankeingänge</div></div>
            <div className="rounded-xl border border-rose-100 bg-rose-50/50 p-4 dark:border-rose-900/50 dark:bg-rose-950/20"><div className="text-2xl font-semibold text-rose-700 dark:text-rose-300">{ledgerCurrency(summary.open_eur)}</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Offen bei belegtem Soll</div></div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 dark:border-emerald-900/50 dark:bg-emerald-950/20"><div className="text-2xl font-semibold text-emerald-700 dark:text-emerald-300">+{ledgerCurrency(summary.surplus_eur)}</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Mehrbetrag über Soll</div></div>
            <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-4 dark:border-violet-900/50 dark:bg-violet-950/20"><div className="text-2xl font-semibold text-violet-700 dark:text-violet-300">{summary.months_with_period_evidence || 0}/12</div><div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Monate mit Zeitraum-Evidence</div></div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 dark:border-amber-900/60 dark:bg-amber-950/20"><div className="text-2xl font-semibold text-amber-800 dark:text-amber-200">{summary.tenant_review_count || 0}</div><div className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">Mieter-Reviews · {summary.period_review_count || 0} Zielperioden · {summary.exception_review_count || 0} Sonderzahlungen</div></div>
          </div>

          <div className="mt-5 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3 text-xs text-blue-800 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-200">Ein fehlender Bankeingang wird nur dann als offen angezeigt, wenn für den Monat sowohl ein Mieterzeitraum als auch ein Stammdaten-Soll belegt sind. Ohne diese Basis bleibt der Status bewusst offen.</div>
          {(summary.tenant_review_count || 0) > 0 && <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100"><strong>Mieterbezogener Smart Review:</strong> Diese Buchungen sind einem Mieter zugeordnet und verbleiben ausschließlich in dessen Mietchronik. Sie erscheinen nicht in der allgemeinen Review-Warteschlange und lösen keine automatische Forderung aus.</div>}

          <section className="mt-5 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-950/60"><div><h2 className="text-sm font-semibold text-gray-900 dark:text-white">Vollständige Zahlungshistorie</h2><p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Monatliche Soll-/Ist-Ansicht mit allen zugeordneten Bankeingängen, Zahlern, IBANs und Zielperioden</p></div><span className="text-xs font-medium text-gray-500 dark:text-gray-400">{months.length} Monate</span></div>
            <div className="overflow-x-auto"><table className="w-full min-w-[1030px] text-sm"><thead className="bg-white text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-slate-900 dark:text-gray-400"><tr><th className="px-4 py-3">Monat</th><th className="px-4 py-3 text-right">Soll · Warmmiete</th><th className="px-4 py-3 text-right">Bankeingang</th><th className="px-4 py-3 text-right">Offen</th><th className="px-4 py-3 text-right">Mehrbetrag</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Evidence / Eingänge</th></tr></thead><tbody className="divide-y divide-slate-100 dark:divide-slate-800">{months.map((month) => <tr key={month.month_key} className="align-top transition-colors hover:bg-violet-50/40 dark:hover:bg-violet-950/15"><td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{month.month_label}</td><td className="px-4 py-3 text-right">{month.expected_eur == null ? 'Soll offen' : <><div className="font-medium text-slate-900 dark:text-white">{ledgerCurrency(month.expected_eur)}</div>{rentComponentLabel(month.cold_rent_eur, month.utilities_advance_eur, month.warm_rent_eur ?? month.expected_eur) && <div className="mt-1 max-w-[300px] text-[11px] leading-4 text-slate-500 dark:text-slate-400">{rentComponentLabel(month.cold_rent_eur, month.utilities_advance_eur, month.warm_rent_eur ?? month.expected_eur)}</div>}</>}</td><td className="px-4 py-3 text-right font-medium text-emerald-700 dark:text-emerald-300">{ledgerCurrency(month.received_eur)}</td><td className="px-4 py-3 text-right font-medium text-rose-700 dark:text-rose-300">{month.outstanding_eur == null ? '–' : ledgerCurrency(month.outstanding_eur)}</td><td className="px-4 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-300">{Number(month.surplus_eur || 0) > 0.009 ? `+${ledgerCurrency(month.surplus_eur)}` : '–'}</td><td className="px-4 py-3"><span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${ledgerTone(month.state)}`}>{ledgerStateLabel(month.state)}</span></td><td className="px-4 py-3"><div className="max-w-md text-xs text-gray-600 dark:text-gray-300">{month.period_evidence_label}</div>{month.state === 'unknown' && <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/20 dark:text-sky-100"><strong>Zahlungsstatus unbekannt</strong> · Bankdaten für {month.month_label} noch nicht vollständig. Clara wertet dies nicht als Mietrückstand.{(month.bank_data_accounts || []).length > 0 && <span className="mt-1 block">Konto: {month.bank_data_accounts.join(', ')} · Bankbelegstand bis {month.bank_data_covered_until ? new Date(month.bank_data_covered_until).toLocaleDateString('de-DE') : 'unbekannt'} · fehlender Auszugszeitraum: {month.month_label}</span>}<button type="button" onClick={() => setShowCsvUpload(true)} className="mt-2 block rounded-md border border-blue-300 bg-blue-100 px-2.5 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-200 dark:border-blue-700 dark:bg-blue-900/40 dark:text-blue-200 dark:hover:bg-blue-900/70">{month.month_label}-Kontoauszug jetzt importieren</button></div>}{month.bank_data_status === 'incomplete' && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100"><strong>Bankdaten noch nicht vollständig</strong> · Der angezeigte Rest für {month.month_label} ist vorläufig (Bankbelegstand bis {month.bank_data_covered_until ? new Date(month.bank_data_covered_until).toLocaleDateString('de-DE') : 'unbekannt'}).</div>}{month.review_items?.length > 0 && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100"><strong>Mieter-Review:</strong> {month.review_items.map((entry) => entry.tenant_review_reason || 'Zahlung prüfen').join(' · ')}</div>}{month.payment_details?.length > 0 && <details className="mt-2"><summary className="cursor-pointer text-xs font-semibold text-violet-700 hover:text-violet-900 dark:text-violet-300 dark:hover:text-violet-100">{month.payment_details.length} Bankeingang/-eingänge anzeigen</summary><div className="mt-2 space-y-1.5">{month.payment_details.map((entry) => <div key={entry.transaction_id} className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs dark:bg-slate-800"><div className="flex flex-wrap items-center gap-x-2 gap-y-1">{entry.trust_label_short && <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${entry.trust_class === 'high' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : entry.trust_class === 'medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>{entry.trust_label_short}</span>}<span className="font-medium text-gray-900 dark:text-white">{entry.booking_date || 'Datum unbekannt'}</span><span className="font-medium text-emerald-700 dark:text-emerald-300">{ledgerCurrency(entry.amount_eur)}</span><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${entry.matched_period ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'}`}>{entry.matched_period ? 'Zielperiode ' + entry.matched_period : 'Zielperiode noch offen'}</span></div><div className="mt-1 text-slate-700 dark:text-slate-200"><strong>Zahler:</strong> {entry.counterparty_name || 'nicht überliefert'} · <strong>IBAN:</strong> <span className="font-mono break-all">{entry.counterparty_iban || 'nicht überliefert'}</span></div><div className="mt-1 text-gray-500 dark:text-gray-400"><strong>Verwendungszweck:</strong> {entry.reference_full || entry.reference_excerpt || 'ohne Referenz'}</div>{entry.trust_label && <div className="mt-1 text-gray-500 dark:text-gray-400">{entry.trust_label}</div>}{entry.tenant_review_required && <span className="mt-1 block font-medium text-amber-800 dark:text-amber-200">Mieter-Review: {entry.tenant_review_reason || 'Zuordnung prüfen'}</span>}{!entry.matched_period && <div className="mt-2 flex flex-wrap items-center gap-2"><select value={targetPeriodByEntry[entry.transaction_id] || entry.period_proposal_month_key || month.month_key || ''} onChange={(e) => setTargetPeriodByEntry((prev) => ({ ...prev, [entry.transaction_id]: e.target.value }))} className="rounded border border-violet-300 bg-white px-2 py-1 text-xs text-slate-800 dark:border-violet-700 dark:bg-slate-900 dark:text-slate-100"><option value="">Zielmonat wählen …</option>{openTargetMonths.map((m) => <option key={m.month_key} value={m.month_key}>{m.month_label}</option>)}</select><button type="button" onClick={() => confirmEntryPeriod(entry, month.month_key)} disabled={confirmingEntryId === entry.transaction_id} className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{confirmingEntryId === entry.transaction_id ? 'Wird bestätigt …' : 'Zahlung bestätigen'}</button></div>}</div>)}</div></details>}</td></tr>)}</tbody></table></div>
          </section>

          {confirmError && <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-200">Bestätigung nicht möglich: {confirmError}</div>}
          {exceptions.length > 0 && <section className="mt-5 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40 dark:border-amber-900/60 dark:bg-amber-950/15"><div className="border-b border-amber-200 px-4 py-3 dark:border-amber-900/60"><h2 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Kautionen und Sonderzahlungen</h2><p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-200/80">Diese Eingänge werden bewusst nicht in die Mieteingangs- oder Sollsaldo-Berechnung einbezogen.</p></div><div className="overflow-x-auto"><table className="w-full min-w-[720px] text-sm"><thead className="text-left text-xs uppercase tracking-wide text-amber-800/80 dark:text-amber-200/80"><tr><th className="px-4 py-3">Buchungstag</th><th className="px-4 py-3 text-right">Betrag</th><th className="px-4 py-3">Einordnung</th><th className="px-4 py-3">Hinweis</th></tr></thead><tbody>{exceptions.map((entry) => <tr key={entry.transaction_id} className="border-t border-amber-100 dark:border-amber-900/50"><td className="px-4 py-3">{entry.booking_date}</td><td className="px-4 py-3 text-right font-medium">{ledgerCurrency(entry.amount_eur)}</td><td className="px-4 py-3">{entry.evidence_label}</td><td className="px-4 py-3 text-xs text-amber-900/80 dark:text-amber-100/80">{entry.reference_excerpt || entry.evidence_note}</td></tr>)}</tbody></table></div></section>}
        </>}
      </div>
    </section>
  </div>;
}


function TenantMonthlyReviewDetailView({ ledger, payment, loading, hasError, onBack, onRetry, onSelectYear, onConfirmRentBasis, onConfirmPeriod, onOpenArrearsCommunication, onOpenPaymentTraffic }) {
  // === Button „Zahlungen zuordnen" — bewusster Klick, nur Zuordnung ===
  const [zuordnenStatus, setZuordnenStatus] = React.useState(null);
  const [zuordnenBusy, setZuordnenBusy] = React.useState(false);
  const [zuordnenReport, setZuordnenReport] = React.useState([]);
  const [zuordnenModal, setZuordnenModal] = React.useState(false);
  const [zuordnenFehler, setZuordnenFehler] = React.useState(null);
  const reportPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(14); doc.text('Zuordnungsreport Mieteingang', 14, 16);
    doc.setFontSize(9); doc.text('Objekt: ' + ((ledger && ledger.property && ledger.property.name) || 'Waldhofstr. 76') + '  |  Erstellt: ' + new Date().toLocaleString('de-DE'), 14, 22);
    doc.setFontSize(8);
    let y = 30;
    doc.setFont(undefined, 'bold'); doc.text('Datum', 14, y); doc.text('Betrag', 38, y); doc.text('Zahler', 60, y); doc.text('Verwendungszweck', 100, y); doc.text('Mieter', 150, y); doc.text('Grund', 180, y);
    doc.setFont(undefined, 'normal');
    zuordnenReport.forEach((r) => {
      y += 6; if (y > 280) { doc.addPage(); y = 20; }
      const d = (r.booking_date || '').slice(0, 10);
      const zweck = (r.reference_raw || '').slice(0, 32);
      const grund = ((r.rent_match_signals || '') + ' (' + (r.rent_match_confidence != null ? r.rent_match_confidence : '-') + ')').slice(0, 26);
      doc.text(d, 14, y); doc.text((r.amount || '') + ' EUR', 38, y); doc.text((r.counterparty_name || '').slice(0, 22), 60, y); doc.text(zweck, 100, y); doc.text((r.matched_tenant_name || '').slice(0, 20), 150, y); doc.text(grund, 180, y);
    });
    doc.save('zuordnungsreport.pdf');
  };
  
  // === Auswahlfenster Zahlungen zuordnen ===
  const handleZuordnen = async () => {
    if (!window.confirm('Zahlungen dieses Zeitraums den Mietern zuordnen? (keine Buchung, nur Zuordnung)')) return;
    setZuordnenBusy(true); setZuordnenFehler(null); setZuordnenStatus('Ordnet zu - bitte warten...');
    try {
      const pid = (ledger && ledger.property && ledger.property.id) ? ledger.property.id : null;
      const body = { property_id: pid };
      if (typeof periodStart !== 'undefined' && periodStart) body.von = periodStart;
      if (typeof periodEnd !== 'undefined' && periodEnd) body.bis = periodEnd;
      const r = await fetch('/api/nk/assign-payments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      setZuordnenBusy(false); setZuordnenReport(j.report || []); setZuordnenModal(true); setZuordnenStatus('Fertig: ' + (j.normalisiert || 0) + ' Namen normalisiert, ' + (j.etiketten || 0) + ' Etiketten, ' + (j.mieter_mit_zahlungen || 0) + ' Mieter mit Zahlungen');
    } catch (e) { setZuordnenBusy(false); setZuordnenFehler(e.message); setZuordnenModal(true); }
  };

  const allMonths = ledger?.months || [];
  // Arbeitsliste statt Historie: Ein bestätigter Kontoauszug ist nicht mehr
  // prüfbar. Er bleibt in Capital/Audit erhalten, erscheint aber weder als
  // Button noch als Beleg in dieser aktiven Monatsprüfung.
  const activeEvidenceForMonth = (month) => (month?.payment_details || []).filter((entry) => !entry?.matched_period || Boolean(entry?.tenant_review_required));
  const isCompletedMonth = (month) => Number(month?.expected_eur || 0) > 0
    && Number(month?.outstanding_eur || 0) <= 0.01
    && activeEvidenceForMonth(month).length === 0;
  const months = allMonths.filter((month) => !isCompletedMonth(month));
  const completedMonthCount = allMonths.length - months.length;
  const availableYears = (() => {
    const nowDate = new Date();
    const nowYear = nowDate.getFullYear();
    const ruleYears = [];
    for (let y = 2025; y <= nowYear; y += 1) ruleYears.push(y);
    if (nowDate.getMonth() === 11) ruleYears.push(nowYear + 1);
    const ledgerYears = Array.isArray(ledger?.available_years) ? ledger.available_years : [];
    const fallback = Number(ledger?.year || payment?.year || nowYear);
    return [...new Set([...ledgerYears, ...ruleYears, fallback])]
      .filter((y) => Number.isInteger(y) && y >= 2000 && y <= 2100)
      .sort((a, b) => b - a);
  })();
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [checkedEvidenceId, setCheckedEvidenceId] = useState(null);
  const [confirmingEvidenceId, setConfirmingEvidenceId] = useState(null);
  const [confirmingRentBasisKey, setConfirmingRentBasisKey] = useState(null);
  const [targetPeriodByTransaction, setTargetPeriodByTransaction] = useState({});
  const [splitByTransaction, setSplitByTransaction] = useState({});
  const [showOpenRentTable, setShowOpenRentTable] = useState(false);
  // --- MIETER-HINWEISE (z.B. Mietminderung): reine Information, keine Forderungswirkung ---
  const [tenantNotes, setTenantNotes] = useState([]);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMsg, setNoteMsg] = useState(null);
  const [noteForm, setNoteForm] = useState({ category: 'mietminderung', noteText: '', reason: '', periodStart: '', periodEnd: '', percent: '', amountEur: '' });
  const noteCategoryLabels = { mietminderung: 'Mietminderung', stundung: 'Stundung', ratenplan: 'Ratenplan', mangelanzeige: 'Mangelanzeige', sonstiges: 'Sonstiges' };
  const fetchTenantNotes = async () => {
    const pid = ledger?.property?.id; const uid = ledger?.unit?.id;
    if (!pid || !uid) { setTenantNotes([]); return; }
    try {
      const payload = await rentPaymentsService.getTenantNotes({ propertyId: pid, unitId: uid });
      setTenantNotes(payload?.notes || []);
    } catch (e) { setTenantNotes([]); }
  };
  useEffect(() => { fetchTenantNotes(); }, [ledger?.property?.id, ledger?.unit?.id]);
  const saveTenantNote = async () => {
    if (!noteForm.noteText.trim()) { setNoteMsg({ kind: 'error', text: 'Bitte einen Hinweistext eingeben.' }); return; }
    setNoteSaving(true); setNoteMsg(null);
    try {
      await rentPaymentsService.createTenantNote({
        propertyId: ledger?.property?.id, unitId: ledger?.unit?.id, tenantName: ledger?.tenant?.name,
        category: noteForm.category, noteText: noteForm.noteText.trim(), reason: noteForm.reason.trim(),
        periodStart: noteForm.periodStart || null, periodEnd: noteForm.periodEnd || null,
        percent: noteForm.percent === '' ? null : Number(noteForm.percent),
        amountEur: noteForm.amountEur === '' ? null : Number(noteForm.amountEur),
      });
      setNoteForm({ category: 'mietminderung', noteText: '', reason: '', periodStart: '', periodEnd: '', percent: '', amountEur: '' });
      setNoteMsg({ kind: 'ok', text: 'Hinweis gespeichert. Reine Information – keine Forderungswirkung, keine Buchung.' });
      await fetchTenantNotes();
    } catch (e) { setNoteMsg({ kind: 'error', text: String(e?.message || 'Speichern fehlgeschlagen.') }); }
    finally { setNoteSaving(false); }
  };
  const deactivateNote = async (id) => {
    try { await rentPaymentsService.deactivateTenantNote({ id }); await fetchTenantNotes(); }
    catch (e) { setNoteMsg({ kind: 'error', text: String(e?.message || 'Deaktivieren fehlgeschlagen.') }); }
  };
  const activeRestrictiveNotes = tenantNotes.filter((n) => ['mietminderung', 'stundung', 'ratenplan'].includes(n.category));
  const isContractHistoryPilot = true; // seit 20.10.2026 fuer alle Objekte (vorher Pilot: Bgm-Fuchs + Neckarauer);
  const ledgerSignature = months.map((month) => month.month_key).join('|');

  useEffect(() => {
    const firstRelevantMonth = months.find((month) => month.expected_eur != null) || months[0] || null;
    setSelectedMonthKey(firstRelevantMonth?.month_key || null);
    setCheckedEvidenceId(null);
    setTargetPeriodByTransaction({});
  }, [ledger?.year, ledgerSignature]);

  const selectedMonth = months.find((month) => month.month_key === selectedMonthKey) || months[0] || null;
  const requiresExplicitTargetPeriodSelection = (entry) => {
    const bookingDay = Number(String(entry?.booking_date || '').slice(8, 10));
    return Boolean(entry && !entry.matched_period && bookingDay >= 25 && !entry.period_proposal_month_key);
  };
  const targetMonthForEvidence = (entry) => {
    const targetPeriod = entry?.matched_period
      || targetPeriodByTransaction[entry?.transaction_id]
      || entry?.period_proposal_month_key
      || (requiresExplicitTargetPeriodSelection(entry) ? null : selectedMonth?.month_key);
    return targetPeriod ? (allMonths.find((month) => month.month_key === targetPeriod) || null) : null;
  };
  const confirmedReceivedForMonth = (month) => Number((month?.payment_details || [])
    .filter((entry) => entry?.matched_period === month?.month_key && !entry?.tenant_review_required)
    .reduce((sum, entry) => sum + Number(entry.amount_eur || 0), 0)
    .toFixed(2));
  const currentMonthKey = new Date().toISOString().slice(0, 7);
  const availableTargetMonths = allMonths.filter((month) => month.expected_eur != null
    && month.month_key <= currentMonthKey
    && confirmedReceivedForMonth(month) < Number(month.expected_eur || 0) - 0.01);
  const allMonthEvidenceItems = selectedMonth?.payment_details || [];
  const evidenceItems = activeEvidenceForMonth(selectedMonth);
  const confirmedEvidenceItems = allMonthEvidenceItems.filter((entry) => entry?.matched_period === selectedMonth?.month_key && !entry?.tenant_review_required);
  const confirmedReceivedEur = Number(confirmedEvidenceItems.reduce((sum, entry) => sum + Number(entry.amount_eur || 0), 0).toFixed(2));
  const remainingOutstandingEur = Number(selectedMonth?.outstanding_eur || 0);
  const hasConfirmedPartial = confirmedEvidenceItems.length > 0 && remainingOutstandingEur > 0.01;
  const expectedEur = selectedMonth?.expected_eur;
  const hasEvidencedExpected = expectedEur != null;
  const workingExpectedEur = selectedMonth?.working_expected_eur;
  const hasWorkingExpected = workingExpectedEur != null;
  // Die aktive Prüfung zeigt nach einer bestätigten Unterzahlung nur noch den echten Rest.
  // Die ursprüngliche Sollmiete bleibt als Aufschlüsselung nachvollziehbar, nicht als offene Forderung.
  const headerShowsOutstandingRest = hasConfirmedPartial;
  const headerAmountEur = headerShowsOutstandingRest ? remainingOutstandingEur : expectedEur;
  const headerTitle = headerShowsOutstandingRest
    ? 'Offener Rest · ' + (selectedMonth?.month_label || '')
    : hasEvidencedExpected
      ? 'Soll-Miete · ' + (selectedMonth?.month_label || '')
      : hasWorkingExpected
        ? 'Soll-Arbeitswert · ' + (selectedMonth?.month_label || '')
        : 'Soll-Miete · ' + (selectedMonth?.month_label || '');
  const selectedMonthComponents = rentComponentLabel(selectedMonth?.cold_rent_eur, selectedMonth?.utilities_advance_eur, selectedMonth?.warm_rent_eur ?? expectedEur);
  const workingMonthComponents = rentComponentLabel(selectedMonth?.working_cold_rent_eur, selectedMonth?.working_utilities_advance_eur, workingExpectedEur);
  const headerBreakdown = headerShowsOutstandingRest
    ? 'Ursprüngliche Soll-Miete ' + ledgerCurrency(expectedEur) + ' · bestätigter Bankeingang ' + ledgerCurrency(confirmedReceivedEur) + ' · verbleibende Forderung ' + ledgerCurrency(remainingOutstandingEur) + '.'
    : hasEvidencedExpected
      ? [selectedMonthComponents, selectedMonth?.period_evidence_label].filter(Boolean).join(' · ')
      : hasWorkingExpected
        ? [workingMonthComponents, 'Aktueller Arbeitswert; Gültigkeit für diesen Monat noch nicht belegt. Kein Saldo und keine Zahlungsstatuswirkung.', selectedMonth?.period_evidence_label].filter(Boolean).join(' · ')
        : [selectedMonthComponents, selectedMonth?.period_evidence_label].filter(Boolean).join(' · ');
  const checkedEvidence = evidenceItems.find((entry) => entry.transaction_id === checkedEvidenceId) || null;
  const evidenceAmount = Number(checkedEvidence?.amount_eur || 0);
  const differenceEur = checkedEvidence ? Number((evidenceAmount - Number(expectedEur || 0)).toFixed(2)) : null;
  const hasCurrentPeriodConfirmation = checkedEvidence?.matched_period === selectedMonth?.month_key;
  const expectedLabel = hasEvidencedExpected ? ledgerCurrency(expectedEur) : hasWorkingExpected ? ledgerCurrency(workingExpectedEur) : 'Sollgrundlage offen';
  const rentBasisConfirmationAvailable = Boolean(selectedMonth?.rent_basis_confirmation_available
    && selectedMonth?.rent_basis_confirmation_transaction_id
    && Number(selectedMonth?.rent_basis_confirmation_amount_eur || 0) > 0);

  const canConfirmEvidence = (entry, confirmationType = 'full_payment') => {
    const targetMonth = targetMonthForEvidence(entry);
    const targetExpectedEur = targetMonth?.expected_eur;
    const amountEur = Number(entry?.amount_eur || 0);
    const fullOrOverpayment = amountEur >= Number(targetExpectedEur || 0) - 0.01;
    const validPartialAmount = amountEur > 0 && amountEur < Number(targetExpectedEur || 0) - 0.01;
    return Boolean(entry
      && targetMonth
      && (!entry.matched_period || (entry.matched_period === targetMonth.month_key && entry.payment_status_review_open))
      && targetExpectedEur != null
      && !['kaution', 'sonderzahlung'].includes(entry.evidence_code)
      && (confirmationType === 'partial_payment' ? validPartialAmount : fullOrOverpayment));
  };
  const runRentBasisConfirmation = async () => {
    if (!rentBasisConfirmationAvailable || !selectedMonth) return;
    const confirmationText = `Sollgrundlage ${ledgerCurrency(selectedMonth.rent_basis_confirmation_amount_eur)} für ${selectedMonth.month_label} bestätigen?\n\nEs wird nichts gebucht, kein Zahlungsstatus gesetzt, kein Saldo freigegeben und keine Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setConfirmingRentBasisKey(selectedMonth.month_key);
    try {
      await onConfirmRentBasis?.({
        transactionId: selectedMonth.rent_basis_confirmation_transaction_id,
        targetPeriod: selectedMonth.month_key,
        expectedAmountEur: selectedMonth.rent_basis_confirmation_amount_eur,
      });
    } catch (error) {
      alert(error.message || 'Sollgrundlage konnte nicht bestätigt werden.');
    } finally {
      setConfirmingRentBasisKey(null);
    }
  };
  const runSingleClickCompletion = async (entry) => {
    const targetMonth = targetMonthForEvidence(entry);
    if (!canConfirmEvidence(entry, 'full_payment') || !targetMonth) return;
    const targetExpectedEur = Number(targetMonth.expected_eur || 0);
    const amountEur = Number(entry.amount_eur || 0);
    const surplusEur = Number(Math.max(0, amountEur - targetExpectedEur).toFixed(2));
    const surplusNote = surplusEur > 0.009
      ? `\n\nMehrbetrag: +${ledgerCurrency(surplusEur)}. Dieser Betrag bleibt sichtbar und wird nicht auf andere Monate übertragen.`
      : '';
    const confirmationText = `Zielmonat ${targetMonth.month_label} und Zahlung bestätigen?\n\nDieser Klick bestätigt den sichtbaren CAMT-Bankeingang für genau diesen Monat. Die offene Forderung wird damit erfüllt und der Beleg aus der aktiven Prüfung entfernt.${surplusNote}\n\nEs wird keine Buchhaltungsbuchung, Mahnung oder Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setConfirmingEvidenceId(entry.transaction_id);
    try {
      await onConfirmPeriod?.({ transactionId: entry.transaction_id, targetPeriod: targetMonth.month_key, confirmationType: 'full_payment' });
    } catch (error) {
      alert(error.message || 'Zielmonat und Zahlung konnten nicht bestätigt werden.');
    } finally {
      setConfirmingEvidenceId(null);
    }
  };
  const runPartialPaymentConfirmation = async (entry) => {
    const targetMonth = targetMonthForEvidence(entry);
    if (!canConfirmEvidence(entry, 'partial_payment') || !targetMonth) return;
    const targetExpectedEur = Number(targetMonth.expected_eur || 0);
    const targetConfirmedReceivedEur = confirmedReceivedForMonth(targetMonth);
    const amountEur = Number(entry.amount_eur || 0);
    const monthReceivedEur = Number((targetConfirmedReceivedEur + amountEur).toFixed(2));
    const outstandingEur = Number(Math.max(0, targetExpectedEur - monthReceivedEur).toFixed(2));
    const surplusEur = Number(Math.max(0, monthReceivedEur - targetExpectedEur).toFixed(2));
    const resultText = outstandingEur > 0.009
      ? `Danach bleiben ${ledgerCurrency(outstandingEur)} als Forderung für ${targetMonth.month_label} in der Zahlungshistorie sichtbar.`
      : surplusEur > 0.009
        ? `Danach ist die Monatsmiete erfüllt; ${ledgerCurrency(surplusEur)} bleiben als nicht verrechnetes Guthaben in der Zahlungshistorie sichtbar.`
        : 'Danach ist die Monatsmiete vollständig bezahlt.';
    const confirmationText = `Zahlung für ${targetMonth.month_label} bestätigen?\n\nSoll: ${ledgerCurrency(targetExpectedEur)}\nBisher bestätigte Bankeingänge: ${ledgerCurrency(targetConfirmedReceivedEur)}\nDieser Bankeingang: ${ledgerCurrency(amountEur)}\n\n${resultText}\n\nEs wird keine Buchhaltungsbuchung, automatische Verrechnung mit anderen Monaten, Mahnung oder Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setConfirmingEvidenceId(entry.transaction_id);
    try {
      await onConfirmPeriod?.({ transactionId: entry.transaction_id, targetPeriod: targetMonth.month_key, confirmationType: 'partial_payment' });
    } catch (error) {
      alert(error.message || 'Unterzahlung konnte nicht bestätigt werden.');
    } finally {
      setConfirmingEvidenceId(null);
    }
  };
  const runPaymentStatusConfirmation = async (entry) => {
    const targetMonth = targetMonthForEvidence(entry);
    if (!entry || !targetMonth) return;
    const confirmationText = `Zahlungsstatus für ${targetMonth.month_label} bestätigen?\n\nSollbetrag, Bankbetrag und Abweichung sind sichtbar. Der Zielmonat ist bereits bestätigt. Es wird keine Buchhaltungsbuchung und keine Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setConfirmingEvidenceId(entry.transaction_id);
    try {
      await onConfirmPeriod?.({ transactionId: entry.transaction_id, targetPeriod: targetMonth.month_key, confirmationType: 'payment_status_only' });
    } catch (error) {
      alert(error.message || 'Zahlungsstatus konnte nicht bestätigt werden.');
    } finally {
      setConfirmingEvidenceId(null);
    }
  };
  const runConfirmation = async (entry = checkedEvidence, confirmationType = 'full_payment') => {
    const targetMonth = targetMonthForEvidence(entry);
    if (!canConfirmEvidence(entry, confirmationType) || !targetMonth) return;
    // Der Klick bestätigt ausschließlich den vorhandenen Kontoauszug für diese
    // Zielperiode; er erzeugt weder Rechnung noch Buchhaltungsbuchung.
    setConfirmingEvidenceId(entry.transaction_id);
    try {
      await onConfirmPeriod?.({ transactionId: entry.transaction_id, targetPeriod: targetMonth.month_key, confirmationType });
    } catch (error) {
      alert(error.message || 'Belegzuordnung konnte nicht bestätigt werden.');
    } finally {
      setConfirmingEvidenceId(null);
    }
  };

  if (showOpenRentTable) return <TenantLedgerDetailView ledger={ledger} payment={payment} loading={loading} hasError={hasError} onBack={() => setShowOpenRentTable(false)} onRetry={onRetry} onSelectYear={onSelectYear} onConfirmPeriod={onConfirmPeriod} onOpenArrearsCommunication={onOpenArrearsCommunication} initialArrearsPreview={false} onCloseArrearsPreview={() => setShowOpenRentTable(false)} />;

  return <div className="mx-auto max-w-5xl">
    <button type="button" onClick={onBack} className="mb-5 inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-violet-300 hover:bg-violet-50 hover:text-violet-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-violet-700 dark:hover:bg-violet-950/30 dark:hover:text-violet-200">Zurück zur Mieteingangskontrolle</button>

    {loading && <div className="flex min-h-64 items-center justify-center rounded-2xl border border-dashed border-violet-200 bg-violet-50/50 p-8 text-sm text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/20 dark:text-violet-200">Monatsprüfung wird aus Capital geladen …</div>}
    {hasError && !loading && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-200"><div className="font-semibold">Monatsprüfung konnte nicht geladen werden.</div><p className="mt-1">Es wurde nichts verändert.</p><Button onClick={onRetry} variant="outline" size="sm" className="mt-3 border-rose-300 bg-white text-rose-700 hover:bg-rose-100">Erneut laden</Button></div>}

    {ledger && !loading && months.length === 0 && <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center text-sm text-emerald-900 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100"><CheckCircle className="mx-auto mb-3 h-7 w-7" /><div className="font-semibold">Keine aktiven Monatsprüfungen</div><p className="mt-1">Alle {completedMonthCount} vollständig geprüften Monate und ihre Belege wurden aus dieser Arbeitsansicht entfernt. Die Auditspur bleibt in Capital erhalten.</p></div>}
    {ledger && !loading && months.length > 0 && <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="border-b border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-700 dark:bg-slate-950/60">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Jahr
            <select value={String(ledger.year)} onChange={(event) => onSelectYear?.(Number(event.target.value))} className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white">
              {availableYears.map((candidate) => <option key={candidate} value={candidate}>{candidate}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Monat
            <select value={selectedMonth?.month_key || ''} onChange={(event) => { setSelectedMonthKey(event.target.value); setCheckedEvidenceId(null); }} className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white">
              {months.map((month) => <option key={month.month_key} value={month.month_key}>{month.month_label}</option>)}
            </select>
          </label>
        </div>
      </div>

      {selectedMonth && <div className="p-5 md:p-7">
        <div className="mb-4 flex flex-wrap gap-2"><Button onClick={() => setShowOpenRentTable(true)} variant="outline" size="sm" className="border-violet-300 bg-white text-violet-800 hover:bg-violet-50 dark:border-violet-800 dark:bg-slate-900 dark:text-violet-200"><WalletCards className="mr-2 h-4 w-4" />Vollständige Zahlungshistorie öffnen</Button>{isContractHistoryPilot && <Button onClick={() => { const query = new URLSearchParams({ property_id: String(ledger?.property?.id || payment?.property_id || ''), unit_id: String(ledger?.unit?.id || payment?.unit_id || ''), tenant_name: String(ledger?.tenant?.name || payment?.tenant_name || '') }); window.location.assign('/miet-nk-historie?' + query.toString()); }} variant="outline" size="sm" className="border-sky-300 bg-white text-sky-800 hover:bg-sky-50 dark:border-sky-800 dark:bg-slate-900 dark:text-sky-200"><FileText className="mr-2 h-4 w-4" />Miet- & NK-Historie öffnen</Button>}{onOpenPaymentTraffic && <Button onClick={onOpenPaymentTraffic} variant="outline" size="sm" className="border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-slate-900 dark:text-emerald-200"><TrendingUp className="mr-2 h-4 w-4" />Zahlungsverlauf</Button>}</div>
        <div className={`rounded-xl border p-4 ${headerShowsOutstandingRest ? 'border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/20' : 'border-rose-200 bg-rose-50 dark:border-rose-900/60 dark:bg-rose-950/20'}`}>
          <div className={`text-xs font-semibold uppercase tracking-wide ${headerShowsOutstandingRest ? 'text-amber-800 dark:text-amber-200' : 'text-rose-700 dark:text-rose-300'}`}>{headerTitle}</div>
          <div className={`mt-1 text-3xl font-bold ${headerShowsOutstandingRest ? 'text-amber-900 dark:text-amber-100' : 'text-rose-800 dark:text-rose-200'}`}>{headerShowsOutstandingRest ? ledgerCurrency(headerAmountEur) : expectedLabel}</div>
          <div className={`mt-2 text-xs ${headerShowsOutstandingRest ? 'text-amber-900/80 dark:text-amber-100/80' : 'text-rose-800/80 dark:text-rose-100/80'}`}>{headerBreakdown}</div>
          {rentBasisConfirmationAvailable && <div className="mt-3 flex flex-wrap items-center gap-3"><Button type="button" onClick={runRentBasisConfirmation} disabled={confirmingRentBasisKey === selectedMonth.month_key} size="sm" className="bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-60">{confirmingRentBasisKey === selectedMonth.month_key ? 'Sollgrundlage wird bestätigt …' : `Sollgrundlage ${ledgerCurrency(selectedMonth.rent_basis_confirmation_amount_eur)} für ${selectedMonth.month_label} bestätigen`}</Button><span className="text-xs text-rose-800/80 dark:text-rose-100/80">Nur Monat und Sollgrundlage; keine Buchung, kein Zahlungsstatus, kein Saldo.</span></div>}
        </div>

        {/* MIETER-HINWEISE (Mietminderung etc.): Anzeige + Eingabe. Reine Information. */}
        <div className="mt-5 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 dark:border-indigo-800/60 dark:bg-indigo-950/20">
          <h3 className="text-sm font-semibold text-indigo-950 dark:text-indigo-100">Mieter-Hinweise (z. B. Mietminderung)</h3>
          <p className="mt-1 text-xs text-indigo-800 dark:text-indigo-200">Reine Information zur Einordnung der Zahlungen: keine Forderungswirkung, keine Verrechnung, keine Buchung.</p>
          {activeRestrictiveNotes.length > 0 && (
            <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100">
              <strong>Hinweis für die Mahnung:</strong> {activeRestrictiveNotes.map((n) => noteCategoryLabels[n.category] || n.category).join(', ')} aktiv – Mahnung/Rechnung vorher prüfen.
            </div>
          )}
          {tenantNotes.length === 0
            ? <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Keine Hinweise hinterlegt.</p>
            : <ul className="mt-2 space-y-2">{tenantNotes.map((n) => (
                <li key={n.id} className="rounded-lg border border-indigo-200 bg-white/80 px-3 py-2 text-xs text-slate-800 dark:border-indigo-800/50 dark:bg-slate-900/40 dark:text-slate-200">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200">{noteCategoryLabels[n.category] || n.category}</span>
                    {(n.period_start || n.period_end) && <span className="text-slate-500 dark:text-slate-400">{n.period_start || '…'} – {n.period_end || 'offen'}</span>}
                    {n.percent != null && <span className="text-slate-500 dark:text-slate-400">{n.percent} %</span>}
                    {n.amount_eur != null && <span className="text-slate-500 dark:text-slate-400">{ledgerCurrency(n.amount_eur)}</span>}
                    <button type="button" onClick={() => deactivateNote(n.id)} className="ml-auto rounded border border-slate-300 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300">Deaktivieren</button>
                  </div>
                  <div className="mt-1">{n.note_text}</div>
                  {n.reason && <div className="mt-1 text-slate-500 dark:text-slate-400">Grund: {n.reason}</div>}
                </li>
              ))}</ul>}
          <div className="mt-3 rounded-lg border border-slate-200 bg-white/70 p-3 dark:border-slate-700 dark:bg-slate-900/30">
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">Neuen Hinweis speichern (bewusster Klick)</div>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              <select value={noteForm.category} onChange={(e) => setNoteForm((f) => ({ ...f, category: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100">
                {Object.entries(noteCategoryLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <input value={noteForm.reason} onChange={(e) => setNoteForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Grund (z. B. Schimmel Bad)" className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
              <input type="date" value={noteForm.periodStart} onChange={(e) => setNoteForm((f) => ({ ...f, periodStart: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
              <input type="date" value={noteForm.periodEnd} onChange={(e) => setNoteForm((f) => ({ ...f, periodEnd: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
              <input type="number" value={noteForm.percent} onChange={(e) => setNoteForm((f) => ({ ...f, percent: e.target.value }))} placeholder="Prozent (optional)" className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
              <input type="number" value={noteForm.amountEur} onChange={(e) => setNoteForm((f) => ({ ...f, amountEur: e.target.value }))} placeholder="Betrag EUR (optional)" className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
            </div>
            <textarea value={noteForm.noteText} onChange={(e) => setNoteForm((f) => ({ ...f, noteText: e.target.value }))} placeholder="Hinweistext, z. B. Mietminderung 15 % wegen Schimmel im Bad" rows={2} className="mt-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100" />
            <button type="button" onClick={saveTenantNote} disabled={noteSaving} className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">{noteSaving ? 'Speichert …' : 'Hinweis speichern'}</button>
            {noteMsg && <div className={`mt-2 text-xs ${noteMsg.kind === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>{noteMsg.text}</div>}
          </div>
        </div>
        <div className="mt-5">
          {zuordnenModal && <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}><div style={{ background: '#fff', borderRadius: 12, padding: 28, maxWidth: 520, width: '90%' }}><h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 10, color: zuordnenFehler ? '#dc2626' : '#16a34a' }}>{zuordnenFehler ? 'Fehler' : 'Erledigt - Zahlungen zugeordnet'}</h3>{zuordnenFehler ? <p style={{ color: '#dc2626' }}>{zuordnenFehler}</p> : <><p style={{ marginBottom: 8 }}><b>{zuordnenReport.length}</b> Bankbelege im gewählten Zeitraum sind zugeordnet.</p><div style={{ maxHeight: 220, overflowY: 'auto', fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>{zuordnenReport.slice(0, 50).map((r, i) => <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}><b>{(r.matched_tenant_name || "?")}</b> - {r.amount} EUR - {(r.reference_raw || "").slice(0, 60)} <span style={{ color: '#6b7280' }}>({r.rent_match_signals || "Name/Vergleich"})</span></div>)}</div></>}<div style={{ marginTop: 16, display: 'flex', gap: 10 }}>{!zuordnenFehler && <button onClick={reportPdf} style={{ padding: '9px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Report als PDF herunterladen</button>}<button onClick={() => setZuordnenModal(false)} style={{ padding: '9px 16px', background: '#e5e7eb', color: '#111827', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Schließen</button></div></div></div>}<h2 className="text-base font-semibold text-slate-900 dark:text-white">Zu prüfende Bankbelege für diesen Monat</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Hier bleiben Kontoauszüge sichtbar, bis Zielmonat und Zahlung durch deinen ausdrücklichen Klick gemeinsam bestätigt sind. Danach verschwinden Beleg und erfüllte Forderung aus dieser Arbeitsliste; die Auditspur bleibt in Capital und der Zahlungshistorie erhalten.</p>
        </div>

        {!hasEvidencedExpected && <div className="mt-4 rounded-xl border border-slate-300 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300"><span className="font-semibold">Grauer Status:</span> Mieterzeitraum oder Sollmiete sind nicht ausreichend belegt. Eine Zielperiodenbestätigung ist deshalb gesperrt.</div>}

        {hasEvidencedExpected && evidenceItems.length === 0 && hasConfirmedPartial && <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100"><div className="font-semibold">Unterzahlung bestätigt</div><div className="mt-1">Bestätigter Bankeingang: {ledgerCurrency(confirmedReceivedEur)} · Soll: {ledgerCurrency(expectedEur)} · <strong>Forderung: {ledgerCurrency(remainingOutstandingEur)}</strong>.</div><div className="mt-1 text-xs">Der bestätigte Kontoauszug bleibt in der Zahlungshistorie. Die Forderung gehört ausschließlich zu diesem Monat; es erfolgt keine automatische Verrechnung.</div></div>}

        {hasEvidencedExpected && evidenceItems.length === 0 && !hasConfirmedPartial && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-100"><span className="font-semibold">Rote Ampel:</span> Für {selectedMonth.month_label} wurde kein passender Bankbeleg gefunden. Es wird kein Smart Review geöffnet, weil kein Beleg vorliegt.</div>}

        <div className="mt-4 space-y-3">
          {evidenceItems.map((entry) => {
            const isChecked = checkedEvidenceId === entry.transaction_id;
            const entryTargetMonth = targetMonthForEvidence(entry);
            const entryExpectedEur = entryTargetMonth?.expected_eur;
            const entryHasEvidencedExpected = entryExpectedEur != null;
            const entryConfirmedReceivedEur = confirmedReceivedForMonth(entryTargetMonth);
            const entryDifference = Number((Number(entry.amount_eur || 0) - Number(entryExpectedEur || 0)).toFixed(2));
            const entryExact = entryHasEvidencedExpected && Math.abs(entryDifference) < 0.01;
            const entryConfirmed = entry.matched_period === entryTargetMonth?.month_key;
            const entryRentCandidate = !['kaution', 'sonderzahlung'].includes(entry.evidence_code);
            const entryPartialCandidate = !entryConfirmed
              && entryHasEvidencedExpected
              && entryRentCandidate
              && Number(entry.amount_eur || 0) > 0
              && Number(entry.amount_eur || 0) < Number(entryExpectedEur || 0) - 0.01;
            const entryMonthReceivedEur = Number((entryConfirmedReceivedEur + Number(entry.amount_eur || 0)).toFixed(2));
            const entryOutstandingEur = Number(Math.max(0, Number(entryExpectedEur || 0) - entryMonthReceivedEur).toFixed(2));
            const entrySurplusEur = Number(Math.max(0, entryMonthReceivedEur - Number(entryExpectedEur || 0)).toFixed(2));
            const entryAmountMismatch = !entryExact && !entryPartialCandidate;
            // Nur dieser eng definierte Review-Grund bedeutet: alle Signale sind richtig,
            // die Engine benötigt lediglich die explizite Zielperiodenbestätigung des Nutzers.
            const entrySafeExactPeriodProposal = !entryConfirmed
              && Number(entry.amount_eur || 0) > 0
              && entryRentCandidate
              && (Boolean(targetPeriodByTransaction[entry.transaction_id])
                || (entry.period_proposal_status === 'proposal_available'
                  && entry.period_proposal_month_key === entryTargetMonth?.month_key)
                || (entry.applied_month_key === selectedMonth.month_key
                  && entry.tenant_review_reason === 'Zielperiode beim Mieter prüfen'));
            const entryMappingMismatch = !entryConfirmed && !entryAmountMismatch && !entryPartialCandidate
              && (!entryRentCandidate || (Boolean(entry.tenant_review_reason) && !entrySafeExactPeriodProposal));
            const entryNeedsReview = Boolean(entry.tenant_review_required) || !entryHasEvidencedExpected;
            const entryPaymentStatusCandidate = Boolean(entryHasEvidencedExpected
              && entryRentCandidate
              && Number(entry.amount_eur || 0) > 0
              && entryConfirmed
              && entry.tenant_review_required
              && entry.target_period_human_confirmed
              && entry.payment_status_review_open);
            const entrySingleClickFullCandidate = Boolean(entryHasEvidencedExpected
              && entryRentCandidate
              && Number(entry.amount_eur || 0) >= Number(entryExpectedEur || 0) - 0.01
              && (entryConfirmed || entrySafeExactPeriodProposal)
              && entry.tenant_review_required
              && !entry.target_period_human_confirmed);
            const entryStatus = entryPaymentStatusCandidate
              ? {
                  kind: 'payment_status_only',
                  label: 'Zahlungsstatus bestätigen',
                  buttonTone: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-500/25',
                  panelTone: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100',
                  detail: `Soll ${ledgerCurrency(entryExpectedEur)} · Bankeingang ${ledgerCurrency(entry.amount_eur)} · Abweichung ${entryDifference >= 0 ? '+' : ''}${ledgerCurrency(entryDifference)}. Dieser separate Klick bestätigt den sichtbaren Zahlungsstatus.`,
                }
              : entrySingleClickFullCandidate
              ? {
                  kind: 'single_click_full',
                  label: `Zielmonat ${entryTargetMonth?.month_label || ''} bestätigen`,
                  buttonTone: 'bg-violet-700 text-white hover:bg-violet-800 shadow-md shadow-violet-500/25',
                  panelTone: 'border-violet-200 bg-violet-50 text-violet-950 dark:border-violet-900/60 dark:bg-violet-950/20 dark:text-violet-100',
                  detail: `Bestätigt ${entryTargetMonth?.month_label || ''} und den sichtbaren Bankeingang gemeinsam. Danach sind Forderung und aktive Belegprüfung erledigt; Historie und Audit bleiben erhalten.`,
                }
              : entryPartialCandidate
              ? {
                  kind: 'partial',
                  label: entryOutstandingEur > 0.009
                    ? `Zielmonat ${entryTargetMonth?.month_label || ''} und Unterzahlung bestätigen · Forderung ${ledgerCurrency(entryOutstandingEur)}`
                    : entrySurplusEur > 0.009
                      ? `Zahlung bestätigen · Guthaben +${ledgerCurrency(entrySurplusEur)}`
                      : 'Zahlung bestätigen · Monat bezahlt',
                  buttonTone: 'bg-amber-400 text-amber-950 hover:bg-amber-500 shadow-md shadow-amber-400/25',
                  panelTone: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100',
                  detail: entryOutstandingEur > 0.009
                    ? `Soll ${ledgerCurrency(entryExpectedEur)} · Monatseingang nach Bestätigung ${ledgerCurrency(entryMonthReceivedEur)} · verbleibende Forderung ${ledgerCurrency(entryOutstandingEur)}.`
                    : entrySurplusEur > 0.009
                      ? `Soll ${ledgerCurrency(entryExpectedEur)} · Monatseingang nach Bestätigung ${ledgerCurrency(entryMonthReceivedEur)} · nicht verrechnetes Guthaben +${ledgerCurrency(entrySurplusEur)}.`
                      : `Soll und bestätigte Monatseingänge entsprechen sich mit ${ledgerCurrency(entryExpectedEur)}.`,
                }
              : entryNeedsReview
              ? {
                  kind: 'mapping',
                  label: 'Review offen',
                  buttonTone: 'bg-slate-600 text-white hover:bg-slate-700 shadow-md shadow-slate-500/25',
                  panelTone: 'border-slate-300 bg-slate-50 text-slate-900 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100',
                  detail: requiresExplicitTargetPeriodSelection(entry) && !entryTargetMonth
                    ? 'Der Beleg liegt am Monatsende und nennt keinen Mietmonat. Bitte den Zielmonat ausdrücklich auswählen.'
                    : !entryHasEvidencedExpected
                    ? 'Der aktuelle Soll-Arbeitswert ist sichtbar; seine Gültigkeit für diesen Monat ist noch nicht belegt.'
                    : entry.payment_status_review_open
                      ? 'Zielmonat ist bestätigt; der Zahlungsstatus bleibt ausdrücklich offen.'
                      : (entry.tenant_review_reason || 'Der Beleg ist weiterhin für die menschliche Prüfung gesperrt.'),
                }
              : entryConfirmed
              ? {
                  kind: 'green',
                  label: 'Prüfung OK',
                  buttonTone: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-500/25',
                  panelTone: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100',
                  detail: 'Beleg, Betrag und Zielmonat sind bestätigt.',
                }
              : entryAmountMismatch
              ? {
                  kind: 'amount',
                  label: 'Betrag weicht ab',
                  buttonTone: 'bg-amber-400 text-amber-950 hover:bg-amber-500 shadow-md shadow-amber-400/25',
                  panelTone: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100',
                  detail: 'Soll ' + ledgerCurrency(entryExpectedEur) + ' · Beleg ' + ledgerCurrency(entry.amount_eur) + ' · Abweichung ' + (entryDifference >= 0 ? '+' : '') + ledgerCurrency(entryDifference) + '.',
                }
              : entryMappingMismatch
              ? {
                  kind: 'mapping',
                  label: 'Zuordnung prüfen',
                  buttonTone: 'bg-rose-600 text-white hover:bg-rose-700 shadow-md shadow-rose-500/25',
                  panelTone: 'border-rose-200 bg-rose-50 text-rose-950 dark:border-rose-900/60 dark:bg-rose-950/20 dark:text-rose-100',
                  detail: entry.tenant_review_reason || 'IBAN, Zahler, Verwendungszweck oder Einheit stimmen nicht ausreichend überein.',
                }
              : {
                  kind: 'green',
                  label: 'Prüfung OK',
                  buttonTone: 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-500/25',
                  panelTone: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-900/60 dark:bg-emerald-950/20 dark:text-emerald-100',
                  detail: 'Soll ' + ledgerCurrency(entryExpectedEur) + ' · Beleg ' + ledgerCurrency(entry.amount_eur) + ' · Abweichung 0,00 €.',
                };
            return <article key={entry.transaction_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-950/30">
              {entry.trust_label_short && <div className="mb-2 flex flex-wrap items-center gap-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${entry.trust_class === 'high' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : entry.trust_class === 'medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>{entry.trust_label_short}</span><span className="text-[11px] text-gray-600 dark:text-gray-300">{entry.trust_label}</span><details className="text-[11px] text-gray-500 dark:text-gray-400"><summary className="cursor-pointer font-semibold">Details &amp; Evidence</summary><div className="mt-1 space-y-0.5"><div>Clara-Confidence: {entry.trust_confidence != null ? `${Math.round(Number(entry.trust_confidence) * 100)} %` : '—'}</div>{(entry.trust_signals || []).map((signal) => <div key={signal}>· {signal}</div>)}<div className="italic">{entry.trust_note}</div></div></details></div>}
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2"><span className="text-lg font-bold text-slate-900 dark:text-white">{ledgerCurrency(entry.amount_eur)}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">bezahlt am {entry.booking_date || 'unbekannt'}</span>{entry.value_date && <span className="text-xs text-slate-500">Wertstellung {entry.value_date}</span>}</div>
                  <dl className="mt-3 grid gap-x-5 gap-y-2 text-sm sm:grid-cols-2"><div><dt className="text-xs text-slate-500">Von</dt><dd className="font-medium text-slate-900 dark:text-white">{entry.counterparty_name || 'nicht überliefert'}</dd></div><div><dt className="text-xs text-slate-500">IBAN</dt><dd className="break-all font-mono text-xs text-slate-800 dark:text-slate-200">{entry.counterparty_iban || 'nicht überliefert'}</dd></div><div className="sm:col-span-2"><dt className="text-xs text-slate-500">Verwendungszweck</dt><dd className="mt-0.5 break-words text-slate-800 dark:text-slate-200">{entry.reference_full || 'nicht überliefert'}</dd></div></dl>
                  {!entry.matched_period && availableTargetMonths.length > 0 && <label className="mt-3 block max-w-sm text-xs font-semibold text-slate-700 dark:text-slate-200">Zielmonat für diesen Bankbeleg
                    <select value={entryTargetMonth?.month_key || ''} onChange={(event) => { setTargetPeriodByTransaction((previous) => ({ ...previous, [entry.transaction_id]: event.target.value })); setCheckedEvidenceId(entry.transaction_id); }} className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-slate-600 dark:bg-slate-900 dark:text-white">
                      {requiresExplicitTargetPeriodSelection(entry) && <option value="" disabled>Zielmonat auswählen …</option>}
                      {availableTargetMonths.map((month) => <option key={month.month_key} value={month.month_key}>{month.month_label} · Soll {ledgerCurrency(month.expected_eur)}</option>)}
                    </select>
                    <span className="mt-1 block font-normal text-slate-500 dark:text-slate-400">{requiresExplicitTargetPeriodSelection(entry) ? 'Monatsendzahlung ohne Monatsangabe: keine automatische Auswahl. ' : ''}Nur offene fällige Monate werden angeboten. Mehrere Belege werden einzeln bestätigt. Nicht gespeichert. Erst der Bestätigungsklick übernimmt diesen Zielmonat; es erfolgt keine automatische Verrechnung.</span>
                  </label>}
                  {!entry.matched_period && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs dark:border-amber-900/60 dark:bg-amber-950/20">
                      <label className="block font-semibold text-amber-900 dark:text-amber-200">
                        Zahlungsart wählen
                        <select
                          className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-amber-800 dark:bg-slate-900 dark:text-slate-100"
                          value={(splitByTransaction[entry.transaction_id]?.art)
                            || (entry.evidence_code === 'kaution' ? 'kaution'
                              : entry.evidence_code === 'kaution_miete_gemischt' ? 'miete_und_kaution'
                                : entry.evidence_code === 'miete_nk_explicit' ? 'miete_nk' : 'miete')}
                          onChange={(event) => {
                            const art = event.target.value;
                            const proposal = art === 'miete_und_kaution'
                              ? proposeMieteKautionSplit(entry, entryExpectedEur)
                              : { miete: Number(entry.amount_eur || 0), kaution: 0 };
                            setSplitByTransaction((previous) => ({ ...previous, [entry.transaction_id]: { art, ...proposal } }));
                          }}
                        >
                          <option value="miete">Miete</option>
                          <option value="miete_nk">Miete + NK</option>
                          <option value="miete_und_kaution">Miete + Kaution</option>
                          <option value="kaution">Kaution</option>
                          <option value="sonderzahlung">Sonderzahlung</option>
                        </select>
                      </label>
                      {(splitByTransaction[entry.transaction_id]?.art || '') === 'miete_und_kaution' && (
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <label className="block">
                            <span className="text-amber-900 dark:text-amber-200">Mietanteil (EUR)</span>
                            <input
                              type="number" step="0.01" min="0"
                              className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-amber-800 dark:bg-slate-900 dark:text-slate-100"
                              value={splitByTransaction[entry.transaction_id]?.miete ?? ''}
                              onChange={(event) => setSplitByTransaction((previous) => ({ ...previous, [entry.transaction_id]: { ...(previous[entry.transaction_id] || {}), miete: Number(event.target.value) } }))}
                            />
                          </label>
                          <label className="block">
                            <span className="text-amber-900 dark:text-amber-200">Kautionsanteil (EUR)</span>
                            <input
                              type="number" step="0.01" min="0"
                              className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-1.5 text-xs text-slate-900 dark:border-amber-800 dark:bg-slate-900 dark:text-slate-100"
                              value={splitByTransaction[entry.transaction_id]?.kaution ?? ''}
                              onChange={(event) => setSplitByTransaction((previous) => ({ ...previous, [entry.transaction_id]: { ...(previous[entry.transaction_id] || {}), kaution: Number(event.target.value) } }))}
                            />
                          </label>
                          <div className="sm:col-span-2">
                            <button
                              type="button"
                              className="rounded bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                              disabled={confirmingEvidenceId === entry.transaction_id}
                              onClick={async () => {
                                const split = splitByTransaction[entry.transaction_id] || {};
                                const targetPeriod = targetPeriodByTransaction[entry.transaction_id] || entryTargetMonth?.month_key || availableTargetMonths[0]?.month_key;
                                if (!targetPeriod) { setCheckedEvidenceId(entry.transaction_id); return; }
                                setConfirmingEvidenceId(entry.transaction_id);
                                try {
                                  await rentPaymentsService.confirmMieteKaution({
                                    propertyId: ledger?.property?.id || payment?.property_id,
                                    unitId: entry.unit_id || ledger?.unit?.id || payment?.unit_id,
                                    tenantName: entry.tenant_name || ledger?.tenant?.name || payment?.tenant_name,
                                    transactionId: entry.transaction_id,
                                    targetPeriod,
                                    mieteAmountEur: split.miete ?? 0,
                                    kautionAmountEur: split.kaution ?? 0,
                                  });
                                  setSplitByTransaction((previous) => ({ ...previous, [entry.transaction_id]: { ...split, art: 'miete_und_kaution', done: true } }));
                                } catch (error) {
                                  setSplitByTransaction((previous) => ({ ...previous, [entry.transaction_id]: { ...split, art: 'miete_und_kaution', error: String(error.message || error) } }));
                                } finally {
                                  setConfirmingEvidenceId(null);
                                }
                              }}
                            >
                              {confirmingEvidenceId === entry.transaction_id ? 'Wird gespeichert …' : 'Aufteilung Miete + Kaution bestätigen'}
                            </button>
                            <p className="mt-1 text-[11px] text-amber-800 dark:text-amber-300">
                              Mietanteil zählt in den Zahlungsverlauf · Kaution zu „Meine Mieter“. Keine Verrechnung, keine Buchung.
                            </p>
                            {splitByTransaction[entry.transaction_id]?.done && (
                              <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">Aufteilung gespeichert.</p>
                            )}
                            {splitByTransaction[entry.transaction_id]?.error && (
                              <p className="mt-1 text-[11px] text-rose-700 dark:text-rose-300">{splitByTransaction[entry.transaction_id].error}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {entry.period_proposal_status === 'proposal_available' && <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/20 dark:text-sky-100"><strong>Periodenvorschlag: {entry.period_proposal_month_key}</strong><span className="ml-1">· {entry.period_proposal_note}</span></div>}
                  {entry.period_proposal_status === 'review_required' && entry.period_proposal_note && <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-300"><strong>Periodenreview:</strong><span className="ml-1">{entry.period_proposal_note}</span></div>}
                </div>
                <button type="button" onClick={() => entryStatus.kind === 'single_click_full' ? runSingleClickCompletion(entry) : entryStatus.kind === 'payment_status_only' ? runPaymentStatusConfirmation(entry) : entryStatus.kind === 'green' && !entryConfirmed ? runConfirmation(entry, 'full_payment') : entryStatus.kind === 'partial' ? runPartialPaymentConfirmation(entry) : setCheckedEvidenceId(entry.transaction_id)} disabled={['single_click_full', 'payment_status_only', 'green', 'partial'].includes(entryStatus.kind) && confirmingEvidenceId === entry.transaction_id} className={`shrink-0 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950 ${entryStatus.buttonTone}`}><span>{['single_click_full', 'payment_status_only', 'green'].includes(entryStatus.kind) ? <CheckCircle className="h-4 w-4" /> : ['amount', 'partial'].includes(entryStatus.kind) ? <AlertCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}</span>{confirmingEvidenceId === entry.transaction_id && entryStatus.kind === 'single_click_full' ? 'Zielmonat und Zahlung werden bestätigt …' : confirmingEvidenceId === entry.transaction_id && entryStatus.kind === 'payment_status_only' ? 'Zahlungsstatus wird bestätigt …' : confirmingEvidenceId === entry.transaction_id && entryStatus.kind === 'partial' ? 'Unterzahlung wird bestätigt …' : entryStatus.label}</button>
              </div>
                            {isChecked && <div className={`mt-4 rounded-lg border p-3 text-sm ${entryStatus.panelTone}`}>
                <div className="flex flex-wrap items-center gap-2"><span className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${entryStatus.buttonTone}`}>{entryStatus.kind === 'green' ? <CheckCircle className="h-4 w-4" /> : ['amount', 'partial'].includes(entryStatus.kind) ? <AlertCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}{entryStatus.label}</span><span className="text-xs">{entryStatus.detail}</span></div>
                {entryStatus.kind === 'mapping' && <details className="mt-3"><summary className="cursor-pointer font-semibold">Prüfdetails öffnen</summary><div className="mt-2 grid gap-1 rounded-md bg-white/70 p-2 text-xs dark:bg-slate-950/50"><div>Bankreferenz: {entry.bank_ref || 'nicht überliefert'}</div><div>End-to-End-Referenz: {entry.end_to_end_id || 'nicht überliefert'}</div><div>Zielperiode: {entry.matched_period || entry.period_proposal_month_key || 'noch offen'}{!entry.matched_period && entry.period_proposal_month_key ? ' (Vorschlag, nicht gespeichert)' : entry.target_period_human_confirmed ? ' (menschlich bestätigt; Zahlungsstatus offen)' : ''}</div><div>Prüfgrund: {entry.tenant_review_reason || 'Zuordnung nicht eindeutig'}</div></div></details>}
                {entryStatus.kind === 'payment_status_only' && <div className="mt-2 text-xs"><strong>Getrennte menschliche Entscheidung:</strong> Der Zielmonat bleibt unverändert; bestätigt wird ausschließlich, dass der bereits vorhandene Bankeingang die Monatsmiete erfüllt. Keine Buchhaltungsbuchung und keine Kommunikation.</div>}
                {entryStatus.kind === 'green' && !entryConfirmed && <div className="mt-2 text-xs"><strong>Keine Buchhaltungsbuchung:</strong> „Prüfung OK“ ordnet ausschließlich den vorhandenen Kontoauszug diesem Mietmonat zu, markiert die Miete als gezahlt und protokolliert die Bestätigung.</div>}
                {entryStatus.kind === 'partial' && <div className="mt-2 text-xs"><strong>Keine automatische Verrechnung:</strong> Der Bankeingang wird ausschließlich diesem Mietmonat zugeordnet. Die verbleibende Forderung oder ein Mehrbetrag erscheint in der bestehenden Zahlungshistorie. Es entstehen keine Buchhaltungsbuchung, Mahnung oder Kommunikation.</div>}
              </div>}
            </article>;
          })}
        </div>
      </div>}
    </section>}
  </div>;
}

const RentTrackingPage = () => {
  const [zaOpen, setZaOpen] = React.useState(false);
  const [zaObjekt, setZaObjekt] = React.useState('');
  const [zaJahr, setZaJahr] = React.useState(new Date().getFullYear());
  const [zaMonat, setZaMonat] = React.useState(new Date().getMonth() + 1);
  const [zaGanzesJahr, setZaGanzesJahr] = React.useState(false);
  const [zaAlle, setZaAlle] = React.useState(true);
  const [zaMieter, setZaMieter] = React.useState([]);
  const [zaBusy, setZaBusy] = React.useState(false);
  const [zaStatus, setZaStatus] = React.useState('');
  const [zaReport, setZaReport] = React.useState(null);
  const [zaFehler, setZaFehler] = React.useState(null);
  const handleZaStart = async () => {
    if (!zaObjekt) { setZaFehler('Bitte Objekt auswählen'); setZaReport({}); return; }
    setZaBusy(true); setZaFehler(null); setZaStatus('Ordnet zu – bitte warten...'); setZaReport(null);
    const von = zaGanzesJahr ? `${zaJahr}-01-01` : `${zaJahr}-${String(zaMonat).padStart(2, '0')}-01`;
    const bis = zaGanzesJahr ? `${zaJahr}-12-31` : new Date(zaJahr, zaMonat, 0).toISOString().slice(0, 10);
    const body = { property_id: zaObjekt, von, bis };
    if (!zaAlle && zaMieter.length) body.mieter = zaMieter;
    try {
      const r = await fetch('/api/nk/assign-payments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      setZaBusy(false);
      if (j.fehler) { setZaFehler(j.fehler); setZaReport({}); return; }
      setZaStatus(`Fertig: ${j.normalisiert || 0} Namen normalisiert, ${j.etiketten || 0} Etiketten, ${(j.report || []).length} Belege zugeordnet.`);
      setZaReport(j);
    } catch (e) { setZaBusy(false); setZaFehler(e.message); setZaReport({}); }
  };
  const zaDownload = () => {
    const rows = zaReport?.report || [];
    const lines = ['Zahlungen-Zuordnung Report', `Objekt: ${zaObjekt}`, `Zeitraum: ${vonText}`, `Erstellt: ${new Date().toLocaleString('de-DE')}`, '', 'Datum;Betrag;Zahler;Verwendungszweck;Zugeordneter Mieter'];
    rows.forEach((r) => lines.push([r.booking_date, r.amount, r.payer_name, (r.reference_raw || '').replace(/;/g, ','), r.matched_tenant_name].join(';')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'zuordnungsreport.csv'; a.click();
  };

  // ============================================================
  // STATE
  // ============================================================
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const zaAlleMieter = React.useMemo(() => {
    const list = (overview?.rows || []).map((e) => e.tenant_name || e.mieter || '').filter(Boolean);
    return Array.from(new Set(list)).sort();
  }, [overview]);
  const [filter, setFilter] = useState('all'); // all | open | overdue | partial | special | bank_review
  
  // Owner/Property filtering
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState('all');
  
  // CSV Upload modal
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [showMatchReview, setShowMatchReview] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  
  // Communication modal
  const [showCommModal, setShowCommModal] = useState(false);
  const [commChannel, setCommChannel] = useState(null);
  const [commPayment, setCommPayment] = useState(null);
  const [commArrearsSnapshot, setCommArrearsSnapshot] = useState(null);
  const [commPurpose, setCommPurpose] = useState(null);
  
  // Smart Review Queue
  const [showSmartReview, setShowSmartReview] = useState(false);
  // Sonderzahlungen
  const [specialPayments, setSpecialPayments] = useState([]);
  const [tenantDetail, setTenantDetail] = useState(null);
  const [unitProfile, setUnitProfile] = useState(null);
  const [unitProfileLoading, setUnitProfileLoading] = useState(false);
  const [unitProfileError, setUnitProfileError] = useState(false);
  const handleUnitProfile = async (payment) => {
    if (!payment?.unit_id) return;
    setUnitProfile({ unit: { unit_label: payment.unit_number ? `Einheit ${payment.unit_number}` : (payment.unit_label || null), tenant_name: payment.tenant_name, property_id: payment.property_id, unit_id: payment.unit_id }, claims: [], payment });
    setUnitProfileLoading(true);
    setUnitProfileError(false);
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      const resp = await fetch(`/api/rent/payment-traffic?unit_id=${encodeURIComponent(payment.unit_id)}`, {
        headers: { 'x-supabase-access-token': session?.access_token || '', 'Content-Type': 'application/json' }
      });
      if (!resp.ok) throw new Error('payment_traffic_failed');
      const data = await resp.json();
      setUnitProfile(data);
    } catch (e) {
      setUnitProfileError(true);
    } finally {
      setUnitProfileLoading(false);
    }
  };
  const handleUnitProfilePdf = async (unit, claim) => {
    if (!unit?.property_id || !claim?.document_id) return;
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      const params = new URLSearchParams({ property_id: String(unit.property_id), document_id: String(claim.document_id) });
      const resp = await fetch(`/api/rent/contract-history/document?${params.toString()}`, {
        headers: { 'x-supabase-access-token': session?.access_token || '', 'Content-Type': 'application/json' }
      });
      if (!resp.ok) throw new Error('document_unavailable');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      window.alert('Dokument derzeit nicht verfügbar.');
    }
  };
  const handleDunFromTraffic = async (payment) => {
    if (!payment) return;
    const snapshot = {
      snapshot_type: 'rent_arrears_v1',
      communication_purpose: 'arrears',
      generated_at: new Date().toISOString(),
      payment,
      tenant: {
        id: payment.tenant_id || '',
        name: payment.tenant_name || '',
        property_id: payment.property_id || '',
        property_name: payment.property_name || '',
      },
      property: { id: payment.property_id || '', name: payment.property_name || '' },
      unit: { id: payment.unit_id || '', identifier: payment.unit_number || '' },
      months: [],
      total_open_eur: Number(payment.outstanding_amount ?? payment.open_amount ?? 0),
      control_note: 'Zahlungsampel: Mahnung wird vorbereitet.',
      test_anchor_transaction_id: payment.is_test_data ? (payment.test_anchor_transaction_id || payment.id) : null,
    };
    setUnitProfile(null);
    await handleOpenArrearsCommunication(snapshot, 'email');
  };
  const [tenantLedgers, setTenantLedgers] = useState({});
  const [inlineLedgerHints, setInlineLedgerHints] = useState({});
  const [ledgerLoadingKey, setLedgerLoadingKey] = useState(null);
  const [ledgerErrorKey, setLedgerErrorKey] = useState(null);
  const [objectAssignmentSelections, setObjectAssignmentSelections] = useState({});
  const [assigningObjectCandidateId, setAssigningObjectCandidateId] = useState(null);
  const [bankAssignmentSelections, setBankAssignmentSelections] = useState({});
  const [assigningBankCandidateId, setAssigningBankCandidateId] = useState(null);
  
  // ============================================================
  // DATA LOADING
  // ============================================================
  
  // Load owners and properties on mount
  useEffect(() => {
    loadOwnersAndProperties();
  }, []);
  
  // Reload data when month, owner, or property changes
  useEffect(() => {
    loadMonthData();
  }, [currentYear, currentMonth, selectedOwner, selectedProperty]);
  

  const loadOwnersAndProperties = async () => {
    try {
      const [ownersData, propertiesData] = await Promise.all([
        propertiesService.getOwners(),
        propertiesService.getProperties()
      ]);
      setOwners(ownersData || []);
      setProperties(propertiesData || []);
    } catch (error) {
      console.error('❌ Fehler beim Laden der Eigentümer/Objekte:', error);
    }
  };
  
  const P7_PERIOD_PROPOSAL_PROPERTY_ID = '31c61013-8e7a-4bfd-b36a-eb94c40a579c';
  const inlineLedgerHintKey = (payment, year = currentYear, month = currentMonth) => [payment.property_id, payment.unit_id, payment.tenant_name, year, month].map(value => String(value || '')).join('::');
  const loadP7InlineLedgerHints = async (rows, year, month) => {
    const targetMonth = `${year}-${String(month).padStart(2, '0')}`;
    const p7Rows = (rows || []).filter((payment) => String(payment.property_id || '') === P7_PERIOD_PROPOSAL_PROPERTY_ID && Boolean(payment.control_review_required));
    const uniqueP7Rows = [...new Map(p7Rows.map((payment) => [inlineLedgerHintKey(payment, year, month), payment])).values()];
    if (uniqueP7Rows.length === 0) return;
    const settled = await Promise.allSettled(uniqueP7Rows.map(async (payment) => {
      const ledger = await rentPaymentsService.getTenantLedger({ propertyId: payment.property_id, unitId: payment.unit_id, tenantName: payment.tenant_name, year });
      const reviewItems = Array.isArray(ledger?.tenant_review_items) ? ledger.tenant_review_items : [];
      const entry = reviewItems.find((item) => String(item.period_proposal_month_key || '') === targetMonth)
        || reviewItems.find((item) => String(item.booking_date || '').slice(0, 7) === targetMonth);
      if (!entry) return [inlineLedgerHintKey(payment, year, month), null];
      if (entry.period_proposal_status === 'proposal_available' && entry.period_proposal_month_key) {
        return [inlineLedgerHintKey(payment, year, month), { status: 'proposal_available', label: `Zielmonat vorgeschlagen: ${entry.period_proposal_month_key} · nicht gespeichert`, note: entry.period_proposal_note || '' }];
      }
      return [inlineLedgerHintKey(payment, year, month), { status: 'review_required', label: `Periodenreview offen${entry.period_proposal_note ? `: ${entry.period_proposal_note}` : ''}`, note: entry.period_proposal_note || '' }];
    }));
    const next = {};
    settled.forEach((result) => {
      if (result.status === 'fulfilled' && result.value?.[1]) next[result.value[0]] = result.value[1];
    });
    setInlineLedgerHints(previous => ({ ...previous, ...next }));
  };

  const loadMonthData = async () => {
    try {
      setLoading(true);
      const data = await rentPaymentsService.getMonthOverview(currentYear, currentMonth);
      
      // Sonderzahlungen speichern
      if (data && data.special_payments) {
        setSpecialPayments(data.special_payments);
      } else {
        setSpecialPayments([]);
      }
      // Rohdaten speichern — Filterung erfolgt in useMemo
      setOverview(data);
      void loadP7InlineLedgerHints(data?.rows || [], currentYear, currentMonth).catch((error) => console.warn('P7-Periodenhinweise konnten nicht geladen werden:', error));
    } catch (error) {
      console.error('❌ Fehler beim Laden:', error);
      alert('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };
  
  // ============================================================
  // MONTH NAVIGATION
  // ============================================================
  
  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentYear(currentYear - 1);
      setCurrentMonth(12);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentYear(currentYear + 1);
      setCurrentMonth(1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  const goToCurrentMonth = () => {
    setCurrentYear(new Date().getFullYear());
    setCurrentMonth(new Date().getMonth() + 1);
  };

  
  const ledgerKeyFor = (payment, year = currentYear) => [payment.property_id, payment.unit_id, payment.tenant_name, year].map(value => String(value || '')).join('::');
  const updateTenantDetailUrl = (payment, mode = 'open', year = currentYear) => {
    const params = new URLSearchParams(window.location.search);
    if (mode === 'open') {
      params.set('tenant', payment.tenant_name || '');
      params.set('tenant_property', payment.property_id || '');
      params.set('tenant_unit', payment.unit_id || '');
      params.set('rent_year', String(year));
    } else {
      ['tenant', 'tenant_property', 'tenant_unit', 'rent_year'].forEach(key => params.delete(key));
    }
    const next = params.toString();
    window.history.replaceState({}, '', next ? window.location.pathname + '?' + next : window.location.pathname);
  };
  const handleTenantLedger = async (payment, requestedYear = currentYear, forceReload = false) => {
    const key = ledgerKeyFor(payment, requestedYear);
    setTenantDetail({ key, payment, year: requestedYear });
    updateTenantDetailUrl(payment, 'open', requestedYear);
    setLedgerErrorKey(null);
    if (tenantLedgers[key] && !forceReload) return;
    setLedgerLoadingKey(key);
    try {
      const ledger = await rentPaymentsService.getTenantLedger({ propertyId: payment.property_id, unitId: payment.unit_id, tenantName: payment.tenant_name, year: requestedYear });
      setTenantLedgers(previous => ({ ...previous, [key]: ledger }));
    } catch (error) {
      console.error('❌ Fehler beim Laden der Mietchronik:', error);
      setLedgerErrorKey(key);
    } finally {
      setLedgerLoadingKey(null);
    }
  };
  const handleBankObjectAssignment = async (candidate) => {
    const selectedPropertyId = String(objectAssignmentSelections[candidate.id] || candidate.suggested_property_id || '');
    const option = (candidate.object_assignment_options || []).find((entry) => String(entry.property_id) === selectedPropertyId);
    if (!option) {
      alert('Bitte zuerst das Zielobjekt auswählen.');
      return;
    }
    const confirmationText = `Bankbeleg ${formatCurrency(candidate.amount)} vom ${candidate.booking_date ? new Date(candidate.booking_date).toLocaleDateString('de-DE') : 'unbekannten Datum'} dem Objekt ${option.property_name} zuordnen?\n\nBestätigt wird ausschließlich das Objekt. Mieter, Einheit, Zielmonat, Zahlungsstatus, Miet-Ist, Saldo und Plusbetrag bleiben offen. Es wird nichts gebucht und keine Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setAssigningObjectCandidateId(candidate.id);
    try {
      await rentPaymentsService.confirmObjectAssignment({ propertyId: option.property_id, transactionId: candidate.transaction_id });
      setObjectAssignmentSelections((previous) => {
        const next = { ...previous };
        delete next[candidate.id];
        return next;
      });
      await loadMonthData();
    } catch (error) {
      console.error('❌ Objektzuordnung fehlgeschlagen:', error);
      alert(error.message || 'Objektzuordnung konnte nicht bestätigt werden.');
    } finally {
      setAssigningObjectCandidateId(null);
    }
  };
  const handleBankTenantAssignment = async (candidate) => {
    const selectedUnitId = String(bankAssignmentSelections[candidate.id] || '');
    const option = (candidate.assignment_options || []).find((entry) => String(entry.unit_id) === selectedUnitId);
    if (!option) {
      alert('Bitte zuerst Mieter und Einheit auswählen.');
      return;
    }
    const confirmationText = `Bankbeleg ${formatCurrency(candidate.amount)} vom ${candidate.booking_date ? new Date(candidate.booking_date).toLocaleDateString('de-DE') : 'unbekannten Datum'} ${option.tenant_name} (${option.unit_identifier}) zuordnen?\n\nBestätigt werden nur Mieter und Einheit. Zielmonat, Zahlungsstatus, Miet-Ist, Saldo und Plusbetrag bleiben offen. Es wird nichts gebucht und keine Kommunikation ausgelöst.`;
    if (!window.confirm(confirmationText)) return;
    setAssigningBankCandidateId(candidate.id);
    try {
      await rentPaymentsService.confirmTenantAssignment({ propertyId: option.property_id, unitId: option.unit_id, tenantName: option.tenant_name, transactionId: candidate.transaction_id });
      setBankAssignmentSelections((previous) => {
        const next = { ...previous };
        delete next[candidate.id];
        return next;
      });
      await loadMonthData();
      const ledgerYear = Number(String(candidate.booking_date || '').slice(0, 4)) || currentYear;
      await handleTenantLedger({ property_id: option.property_id, unit_id: option.unit_id, tenant_name: option.tenant_name }, ledgerYear, true);
    } catch (error) {
      console.error('❌ Mieterzuordnung fehlgeschlagen:', error);
      alert(error.message || 'Mieterzuordnung konnte nicht bestätigt werden.');
    } finally {
      setAssigningBankCandidateId(null);
    }
  };
  const handleTenantLedgerRentBasisConfirmation = async ({ transactionId, targetPeriod, expectedAmountEur }) => {
    if (!tenantDetail?.payment) return;
    const detailPayment = tenantDetail.payment;
    await rentPaymentsService.confirmTenantLedgerRentBasis({ propertyId: detailPayment.property_id, unitId: detailPayment.unit_id, tenantName: detailPayment.tenant_name, transactionId, targetPeriod, expectedAmountEur });
    const targetYear = Number(String(targetPeriod).slice(0, 4));
    await handleTenantLedger(detailPayment, targetYear, true);
    await loadMonthData();
  };
  const handleTenantLedgerPeriodConfirmation = async ({ transactionId, targetPeriod, confirmationType = 'full_payment' }) => {
    if (!tenantDetail?.payment) return;
    const detailPayment = tenantDetail.payment;
    await rentPaymentsService.confirmTenantLedgerPeriod({ propertyId: detailPayment.property_id, unitId: detailPayment.unit_id, tenantName: detailPayment.tenant_name, transactionId, targetPeriod, confirmationType });
    const targetYear = Number(String(targetPeriod).slice(0, 4));
    await handleTenantLedger(detailPayment, targetYear, true);
    await loadMonthData();
  };
  const closeTenantDetail = () => {
    updateTenantDetailUrl({}, 'close');
    setTenantDetail(null);
    setLedgerErrorKey(null);
  };


  // ============================================================
  // PAYMENT ACTIONS
  // ============================================================
  
  const runTestAction = async (payment, action, amount = undefined) => {
    const result = await rentPaymentsService.performTestAction(action, payment.id, amount);
    await loadMonthData();
    return result;
  };

  const handleQuickConfirm = async (payment) => {
    if (!confirm(`Miete von ${payment.tenant_name} als vollständig eingegangen markieren?`)) return;
    try {
      if (payment.is_test_data) {
        await runTestAction(payment, 'confirm');
        alert('🧪 Testzahlung als bezahlt bestätigt. Nur Canary-Daten wurden aktualisiert.');
      } else {
        await rentPaymentsService.quickConfirm(payment.id, payment.expected_amount);
        await loadMonthData();
      }
    } catch (error) {
      console.error('❌ Fehler:', error);
      alert('Fehler beim Bestätigen');
    }
  };

  const handlePartialPayment = async (payment) => {
    const amount = prompt(`Teilzahlung für ${payment.tenant_name} (Erwartet: ${formatCurrency(payment.expected_amount)})`);
    if (!amount) return;
    const parsedAmount = parseFloat(amount.replace(',', '.'));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      alert('Ungültiger Betrag');
      return;
    }
    try {
      if (payment.is_test_data) {
        await runTestAction(payment, 'partial', parsedAmount);
        alert('🧪 Testteilzahlung gespeichert. Nur Canary-Daten wurden aktualisiert.');
      } else {
        await rentPaymentsService.recordPartialPayment(payment.id, parsedAmount);
        await loadMonthData();
      }
    } catch (error) {
      console.error('❌ Fehler:', error);
      alert('Fehler beim Erfassen der Teilzahlung');
    }
  };

  const handleReopen = async (payment) => {
    const reason = prompt(`Grund für Wiedereröffnung (${payment.tenant_name}):`);
    if (!reason) return;
    try {
      if (payment.is_test_data) {
        await runTestAction(payment, 'reopen');
        alert('🧪 Testzahlung wiedereröffnet. Nur Canary-Daten wurden aktualisiert.');
      } else {
        await rentPaymentsService.reopenPayment(payment.id, reason);
        await loadMonthData();
      }
    } catch (error) {
      console.error('❌ Fehler:', error);
      alert('Fehler beim Wiedereröffnen');
    }
  };

  const handleTestCommunicationDryRun = async () => {
    if (!commPayment?.is_test_data) return;
    const anchorId = commArrearsSnapshot?.test_anchor_transaction_id || commPayment.id;
    await rentPaymentsService.performTestAction('communication_preview', anchorId);
    alert('🧪 Kommunikations-Dry-Run protokolliert. Es wurde keine E-Mail oder WhatsApp-Nachricht versendet.');
  };

  const handleOpenArrearsCommunication = async (snapshot, channel) => {
    const detailPayment = snapshot?.payment || tenantDetail?.payment || {};
    if (detailPayment.communication_blocked || snapshot?.communication_blocked) {
      alert('Kommunikation ist gesperrt: Sollbasis, Zielperiode und Raten-/Sonderzuordnung müssen zuerst geprüft werden.');
      return;
    }
    const anchorId = snapshot.test_anchor_transaction_id || detailPayment.id;
    let testContactEmail = null;
    let productionContactEmail = null;
    if (detailPayment.is_test_data && anchorId) {
      try {
        const contact = await rentPaymentsService.getTestEmailContact(anchorId);
        testContactEmail = contact?.email || null;
      } catch (error) {
        alert('Testkontakt konnte nicht geladen werden: ' + error.message);
        return;
      }
    } else if (snapshot?.tenant?.id) {
      try {
        const contact = await rentPaymentsService.productionEmailContact('get_contact', snapshot.tenant.id);
        productionContactEmail = contact?.email || null;
      } catch (error) {
        alert('Mieter-E-Mail konnte nicht geprüft werden: ' + error.message);
        return;
      }
    }
    setCommArrearsSnapshot(snapshot);
    setCommPurpose('arrears');
    setCommPayment({
      ...detailPayment,
      id: anchorId || ('arrears_snapshot_' + snapshot.generated_at),
      tenant_id: snapshot.tenant.id || detailPayment.tenant_id,
      tenant_name: snapshot.tenant.name || detailPayment.tenant_name,
      tenant_email: testContactEmail || productionContactEmail || detailPayment.tenant_email || null,
      tenant_phone: detailPayment.tenant_phone || null,
      property_id: snapshot.tenant.property_id || detailPayment.property_id,
      property_name: snapshot.tenant.property_name || detailPayment.property_name,
      property_address: detailPayment.property_address,
      property_city: detailPayment.property_city,
      is_test_data: Boolean(detailPayment.is_test_data),
      test_anchor_transaction_id: anchorId || null,
    });
    setCommChannel(channel);
    setShowCommModal(true);
  };

  const handleOpenPaymentReconciliationEmail = async (payment) => {
    const periodYear = Number(payment.period_year || currentYear);
    const periodMonth = Number(payment.period_month || currentMonth);
    const periodLabel = `${getMonthName(periodMonth)} ${periodYear}`;
    const reconciliationSnapshot = {
      snapshot_type: 'rent_payment_reconciliation_v1',
      communication_purpose: 'payment_reconciliation',
      generated_at: new Date().toISOString(),
      tenant: { id: payment.tenant_id || '', name: payment.tenant_name || '', property_id: payment.property_id || '', property_name: payment.property_name || '' },
      property: { id: payment.property_id || '', name: payment.property_name || '' },
      unit: { id: payment.unit_id || '', identifier: payment.unit_number || '' },
      months: [{ month_key: `${periodYear}-${String(periodMonth).padStart(2, '0')}`, month_label: periodLabel, expected_eur: Number(payment.expected_amount || 0), received_eur: Number(payment.actual_amount || payment.received_amount || 0), outstanding_eur: 0, state: 'payment_reconciliation', note: payment.control_note || 'Sollbasis und Zielperiode werden geprüft.' }],
      total_open_eur: 0,
      control_note: payment.control_note || 'Sollbasis und Zielperiode werden geprüft.',
      test_anchor_transaction_id: payment.is_test_data ? (payment.test_anchor_transaction_id || payment.id) : null,
    };
    let testContactEmail = null;
    let productionContactEmail = null;
    try {
      if (payment.is_test_data) {
        const contact = await rentPaymentsService.getTestEmailContact(payment.test_anchor_transaction_id || payment.id);
        testContactEmail = contact?.email || null;
      } else if (payment.tenant_id) {
        const contact = await rentPaymentsService.productionEmailContact('get_contact', payment.tenant_id);
        productionContactEmail = contact?.email || null;
      }
    } catch (error) {
      alert('Mieter-E-Mail konnte nicht geprüft werden: ' + error.message);
      return;
    }
    setCommArrearsSnapshot(reconciliationSnapshot);
    setCommPurpose('payment_reconciliation');
    setCommPayment({ ...payment, tenant_email: testContactEmail || productionContactEmail || payment.tenant_email || null, is_test_data: Boolean(payment.is_test_data), test_anchor_transaction_id: payment.is_test_data ? (payment.test_anchor_transaction_id || payment.id) : null });
    setCommChannel('email');
    setShowCommModal(true);
  };

  const handleTestWhatsAppLocalValidate = async ({ phone, message, arrearsSnapshot, includePdf }) => {
    const anchorId = arrearsSnapshot?.test_anchor_transaction_id || commPayment?.id;
    if (!anchorId) throw new Error('Test-WhatsApp-Anker fehlt');
    const result = await rentPaymentsService.validateTestWhatsAppLocal({
      transactionId: anchorId,
      phone,
      message,
      snapshot: arrearsSnapshot,
      includePdf,
    });
    return result;
  };

  const handleTestEmailReview = async ({ email, subject, message, arrearsSnapshot, includePdf }) => {
    const anchorId = arrearsSnapshot?.test_anchor_transaction_id || commPayment?.id;
    if (!anchorId) throw new Error('Test-E-Mail-Anker fehlt');
    return rentPaymentsService.startTestEmailReview({
      transactionId: anchorId,
      email,
      subject,
      message,
      snapshot: arrearsSnapshot,
      includePdf,
    });
  };

  const handleTestEmailSend = async ({ email, subject, message, arrearsSnapshot, reviewToken, includePdf }) => {
    const anchorId = arrearsSnapshot?.test_anchor_transaction_id || commPayment?.id;
    if (!anchorId) throw new Error('Test-E-Mail-Anker fehlt');
    return rentPaymentsService.sendTestEmail({
      transactionId: anchorId,
      email,
      subject,
      message,
      snapshot: arrearsSnapshot,
      reviewToken,
      includePdf,
    });
  };

  const handleProductionContactSave = async ({ email }) => {
    if (!commPayment?.tenant_id) throw new Error('Mieterkennung fehlt');
    const result = await rentPaymentsService.productionEmailContact('set_contact', commPayment.tenant_id, { email, save_confirmed: true });
    setCommPayment(current => current ? { ...current, tenant_email: result.email } : current);
    return result;
  };

  const handleProductionEmailReview = async ({ email, subject, message, arrearsSnapshot, includePdf, recipientConfirmed }) => {
    const tenantId = arrearsSnapshot?.tenant?.id || commPayment?.tenant_id;
    const transactionId = commPayment?.id;
    if (!tenantId || !transactionId) throw new Error('Mieter- oder Zahlungsanker fehlt');
    return rentPaymentsService.startProductionEmailReview({ tenantId, transactionId, email, recipientConfirmed, subject, message, snapshot: arrearsSnapshot, includePdf });
  };

  const handleProductionEmailSend = async ({ email, subject, message, arrearsSnapshot, reviewToken, includePdf, recipientConfirmed }) => {
    const tenantId = arrearsSnapshot?.tenant?.id || commPayment?.tenant_id;
    const transactionId = commPayment?.id;
    if (!tenantId || !transactionId) throw new Error('Mieter- oder Zahlungsanker fehlt');
    return rentPaymentsService.sendProductionEmail({ tenantId, transactionId, email, recipientConfirmed, subject, message, snapshot: arrearsSnapshot, reviewToken, includePdf });
  };

  // ============================================================
  // BOOTSTRAP
  // ============================================================
  
  const handleBootstrap = async () => {
    if (!confirm(`Automatisch Zahlungen für ${getMonthName(currentMonth)} ${currentYear} erstellen?`)) {
      return;
    }
    
    try {
      const count = await rentPaymentsService.bootstrapMonth(currentYear, currentMonth);
      alert(`${count} Zahlungen erstellt`);
      await loadMonthData();
    } catch (error) {
      console.error('❌ Fehler:', error);
      alert('Fehler beim Bootstrap');
    }
  };
  
  const handleSync = async () => {
    if (!confirm(`Sollbeträge für ${currentYear} aus Stammdaten aktualisieren?\n\nNur offene Zahlungen werden angepasst.`)) {
      return;
    }
    
    try {
      const result = await rentPaymentsService.syncExpectedAmounts(currentYear);
      if (result.updated_count > 0) {
        alert(`✅ ${result.updated_count} Sollbeträge aktualisiert`);
      } else {
        alert('✅ Alle Sollbeträge bereits aktuell');
      }
      await loadMonthData();
    } catch (error) {
      console.error('❌ Fehler:', error);
      alert('Fehler beim Sync: ' + error.message);
    }
  };
  
  // ============================================================
  // HELPERS
  // ============================================================
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount || 0);
  };
  
  const getMonthName = (month) => {
    const names = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    return names[month - 1];
  };
  
  const getStateColor = (state) => {
    switch (state) {
      case 'paid': return 'text-green-600 bg-green-50';
      case 'overpaid': return 'text-green-700 bg-emerald-50';
      case 'nk_credit_deduction': return 'text-green-700 bg-emerald-50';
      case 'partial': return 'text-yellow-600 bg-yellow-50';
      case 'overdue': return 'text-red-600 bg-red-50';
      case 'open': return 'text-blue-600 bg-blue-50';
      case 'period_allocation_review': return 'text-yellow-600 bg-yellow-50 dark:text-yellow-300 dark:bg-yellow-900/30';
      case 'special_payment': return 'text-purple-600 bg-purple-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };
  
  const getStateIcon = (state) => {
    switch (state) {
      case 'paid': return <CheckCircle className="w-5 h-5" />;
      case 'overpaid': return <CheckCircle className="w-5 h-5" />;
      case 'nk_credit_deduction': return <CheckCircle className="w-5 h-5" />;
      case 'partial': return <AlertCircle className="w-5 h-5" />;
      case 'overdue': return <XCircle className="w-5 h-5" />;
      case 'open': return <Clock className="w-5 h-5" />;
      case 'period_allocation_review': return <AlertCircle className="w-5 h-5" />;
      case 'special_payment': return <Euro className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };
  
  const getStateLabel = (state) => {
    switch (state) {
      case 'paid': return 'Bezahlt';
      case 'overpaid': return 'Bezahlt · Mehrbetrag';
      case 'nk_credit_deduction': return 'Bezahlt · abzüglich NK-Guthaben';
      case 'partial': return 'Teilzahlung';
      case 'overdue': return 'Überfällig';
      case 'open': return 'Offen';
      case 'period_allocation_review': return 'Prüfung offen';
      case 'settlement_letter_review': return 'NK-Abrechnung';
      case 'settlement_claim_open': return 'NK-Abrechnung offen';
      case 'settlement_claim_partial': return 'NK-Abrechnung · Teilzahlung';
      case 'special_payment': return 'Sonderzahlung';
      default: return state;
    }
  };

  // Genau ein Prüf-Button pro Mietmonat: grün nur bei vollständiger, belegter
  // Erfüllung; gelb nur bei belastbarer Betragsabweichung; rot bei ungeklärter
  // Zuordnung oder unzureichender Evidence. Diese Darstellung schreibt keine Daten.
  const getPaymentReviewButton = (payment) => {
    if (payment.is_settlement_claim) return {
      tone: 'bg-amber-100 text-amber-900 shadow-sm dark:bg-amber-900/30 dark:text-amber-100',
      icon: <Clock className="h-4 w-4" />,
      label: 'NK-Abrechnung offen',
      detail: 'Offene Restforderung laut menschlich bestätigtem Abrechnungsschreiben. Kein automatischer Zahlungsstatus und keine Kommunikation.',
    };
    if (payment.is_settlement_letter_review) return {
      tone: 'bg-violet-100 text-violet-800 shadow-sm hover:bg-violet-200 focus-visible:ring-violet-500 dark:bg-violet-900/30 dark:text-violet-100 dark:hover:bg-violet-900/50',
      icon: <FileText className="h-4 w-4" />,
      label: 'NK-Abrechnung',
      detail: 'Gesamtforderung laut Abrechnungsschreiben; keine Zahlungszuordnung.' ,
    };
    const toCents = (value) => Math.round((Number(value) || 0) * 100);
    const expectedCents = toCents(payment.expected_amount);
    const settledCents = toCents(payment.settled_amount ?? payment.received_amount);
    const outstandingCents = Math.max(0, toCents(payment.outstanding_amount));
    const surplusCents = Math.max(0, toCents(payment.surplus_amount ?? ((payment.settled_amount ?? payment.received_amount) - payment.expected_amount)));
    const hasReceiptEvidence = Number(payment.matched_count || 0) > 0 || (payment.payment_details || []).length > 0;
    const needsEvidenceReview = Boolean(payment.control_review_required)
      || payment.derived_state === 'period_allocation_review';
    const isFullySettled = expectedCents > 0 && settledCents >= expectedCents && outstandingCents <= 1;
    const tenantReviewReceiptCount = Number(payment.tenant_review_bank_receipt_count || 0);

    if (tenantReviewReceiptCount > 0) {
      return {
        tone: 'bg-amber-400 text-amber-950 shadow-lg shadow-amber-400/25 hover:bg-amber-500 focus-visible:ring-amber-500',
        icon: <FileText className="h-4 w-4" />,
        label: tenantReviewReceiptCount === 1 ? 'Bankbeleg prüfen' : `${tenantReviewReceiptCount} Bankbelege prüfen`,
        detail: `${tenantReviewReceiptCount} CAMT-Prüfbeleg${tenantReviewReceiptCount === 1 ? '' : 'e'} dem Mieter zugeordnet. Zielmonat und Zahlungsstatus bleiben offen; kein Miet-Ist und keine Verrechnung.`,
      };
    }

    if (!needsEvidenceReview && hasReceiptEvidence && isFullySettled) {
      return {
        tone: 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-700 focus-visible:ring-emerald-500',
        icon: <CheckCircle className="h-4 w-4" />,
        label: surplusCents > 0 ? `Prüfung OK · +${formatCurrency(surplusCents / 100)}` : 'Prüfung OK',
        detail: surplusCents > 0 ? `${formatCurrency(surplusCents / 100)} mehr als Soll; der Mehrbetrag bleibt in der Zahlungshistorie sichtbar.` : 'Beleg, Einheit und Betrag stimmen überein.',
      };
    }

    if (!needsEvidenceReview && hasReceiptEvidence && (payment.derived_state === 'partial' || outstandingCents > 1)) {
      return {
        tone: 'bg-amber-400 text-amber-950 shadow-lg shadow-amber-400/25 hover:bg-amber-500 focus-visible:ring-amber-500',
        icon: <AlertCircle className="h-4 w-4" />,
        label: 'Betrag weicht ab',
        detail: formatCurrency(outstandingCents / 100) + ' offen – Soll und belegter Eingang weichen ab.',
      };
    }

    // Ohne vorliegenden Beleg gibt es nichts zuzuordnen. Diese Lage wird klar
    // von einem vorhandenen, aber mehrdeutigen Bankbeleg getrennt dargestellt.
    if (!needsEvidenceReview && !hasReceiptEvidence) {
      return {
        tone: 'bg-rose-600 text-white shadow-lg shadow-rose-500/25 hover:bg-rose-700 focus-visible:ring-rose-500',
        icon: <XCircle className="h-4 w-4" />,
        label: 'Kein Bankbeleg',
        detail: 'Für diesen Mietmonat liegt im aktuell importierten Bankbestand kein passender Zahlungseingang vor.',
      };
    }

    return {
      tone: 'bg-rose-600 text-white shadow-lg shadow-rose-500/25 hover:bg-rose-700 focus-visible:ring-rose-500',
      icon: <XCircle className="h-4 w-4" />,
      label: 'Zuordnung prüfen',
      detail: needsEvidenceReview
        ? 'IBAN, Verwendungszweck, Einheit oder Zielmonat sind noch nicht eindeutig belegt.'
        : 'IBAN, Zahler, Verwendungszweck oder Einheit stimmen nicht ausreichend überein.',
    };
  };
  
  // Gefilterte Daten als useMemo — reagiert stets auf die aktuellen Properties und Eigentümerauswahl.
  // Gemeinsame Bankquellen bleiben bis zur menschlichen Objektbestätigung in einer
  // globalen Warteschlange und erscheinen nicht vorab unter irgendeinem Objekt.
  const [selectedTenant, setSelectedTenant] = useState('all'); // 15.10.2026: Mieter-Dropdown
  const filteredOverview = useMemo(() => {
    if (!overview || !overview.rows) return null;

    const matchesCurrentSelection = (item) => {
      if (selectedOwner !== 'all') {
        // Fail closed: Solange die Objektzuordnung noch nicht geladen ist, keine fremden Daten anzeigen.
        if (properties.length === 0) return false;
        const property = properties.find(p => p.id === item.property_id);
        if (!property || property.owner_entity !== selectedOwner) return false;
      }
      if (selectedTenant !== 'all' && String(item.tenant_name || '') !== selectedTenant) return false;
      return selectedProperty === 'all' || item.property_id === selectedProperty;
    };

    const rows = overview.rows.filter(matchesCurrentSelection);
    const filteredSpecialPayments = (overview.special_payments || []).filter(matchesCurrentSelection);
    const filteredHistoricalRentPayments = (overview.historical_rent_payments || []).filter(matchesCurrentSelection);
    const allUnassignedObjectCandidates = overview.unassigned_object_candidates || [];
    const filteredUnassignedObjectCandidates = selectedOwner === 'all' && selectedProperty === 'all'
      ? allUnassignedObjectCandidates
      : allUnassignedObjectCandidates.filter((candidate) => {
          const suggestedPropertyId = String(candidate.suggested_property_id || '');
          if (!suggestedPropertyId) return false;
          return matchesCurrentSelection({ property_id: suggestedPropertyId });
        });
    const filteredUnassignedPaymentCandidates = (overview.unassigned_payment_candidates || []).filter(matchesCurrentSelection);

    const operationalRows = rows.filter(p => !p.is_settlement_letter_review && !p.is_settlement_claim && !p.is_vacant);
    const rowsWithCompleteComponents = operationalRows.filter(p => p.rent_component_status === 'complete');
    const summary = {
      expected: operationalRows.reduce((sum, p) => sum + parseFloat(p.expected_amount || 0), 0),
      cold_rent_total: rowsWithCompleteComponents.reduce((sum, p) => sum + parseFloat(p.cold_rent_amount || 0), 0),
      utilities_advance_total: rowsWithCompleteComponents.reduce((sum, p) => sum + parseFloat(p.utilities_advance_amount || 0), 0),
      rent_component_complete_count: rowsWithCompleteComponents.length,
      received: operationalRows.reduce((sum, p) => sum + parseFloat(p.received_amount || 0), 0),
      nk_credit_total: operationalRows.reduce((sum, p) => sum + parseFloat(p.nk_credit_amount || 0), 0),
      settled: operationalRows.reduce((sum, p) => sum + parseFloat(p.settled_amount ?? p.received_amount ?? 0), 0),
      open: 0,
      quote: 0,
      paid_count: operationalRows.filter(p => ['paid', 'nk_credit_deduction'].includes(p.derived_state)).length,
      partial_count: operationalRows.filter(p => p.derived_state === 'partial').length,
      overdue_count: operationalRows.filter(p => p.derived_state === 'overdue').length,
      open_count: operationalRows.filter(p => p.derived_state === 'open' || p.derived_state === 'pending').length,
      total_count: operationalRows.length,
      vacant_unit_count: rows.filter(p => p.is_vacant).length,
      settlement_letter_review_count: rows.filter(p => p.is_settlement_letter_review).length,
      special_payment_count: filteredSpecialPayments.length,
      special_payment_total: filteredSpecialPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      historical_rent_payment_count: filteredHistoricalRentPayments.length,
      historical_rent_payment_total: filteredHistoricalRentPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
      unassigned_object_candidate_count: filteredUnassignedObjectCandidates.length,
      unassigned_payment_candidate_count: filteredUnassignedPaymentCandidates.length,
      tenant_review_bank_receipt_count: operationalRows.reduce((sum, p) => sum + Number(p.tenant_review_bank_receipt_count || 0), 0),
    };
    // Die Ist-Einnahmen sind ausschließlich Bankeingänge. Die Quote berücksichtigt
    // zusätzlich bestätigte NK-Gutschriften als Erfüllung der Mietforderung.
    summary.open = Math.max(0, summary.expected - summary.settled);
    summary.quote = summary.expected > 0 ? summary.settled / summary.expected : 0;

    return { rows, specialPayments: filteredSpecialPayments, historicalRentPayments: filteredHistoricalRentPayments, unassignedObjectCandidates: filteredUnassignedObjectCandidates, unassignedPaymentCandidates: filteredUnassignedPaymentCandidates, summary };
  }, [overview, properties, selectedOwner, selectedProperty, selectedTenant]);

  // Mieter-Filter zurücksetzen, wenn der Mieter im gewählten Objekt/Eigentümer nicht mehr vorkommt
  useEffect(() => {
    if (selectedTenant === 'all') return;
    const stillInScope = (overview?.rows || []).some((row) => {
      if (String(row.tenant_name || '') !== selectedTenant) return false;
      if (selectedOwner !== 'all') {
        if (properties.length === 0) return false;
        const property = properties.find(p => p.id === row.property_id);
        if (!property || property.owner_entity !== selectedOwner) return false;
      }
      return selectedProperty === 'all' || row.property_id === selectedProperty;
    });
    if (!stillInScope) setSelectedTenant('all');
  }, [overview, properties, selectedOwner, selectedProperty, selectedTenant]);

  const isResolvedMonth = (row) => ['paid', 'nk_credit_deduction'].includes(row.derived_state) && !row.control_review_required;
  const filteredRows = (overview?.rows || []).filter(row => {
    // Die Standardansicht ist eine aktive Arbeitsliste. Erfolgreich geprüfte
    // Monate und ihre Belege sind weiterhin im Audit vorhanden, aber hier weg.
    if (filter === 'all') return !isResolvedMonth(row);
    if (filter === 'special') return false;
    if (filter === 'vacant') return row.is_vacant === true;
    // Im Bankbeleg-Prüfmodus bleiben alle Monatszeilen sichtbar. Dadurch stehen
    // Sollmiete, Ist-Stand und die noch offenen CAMT-Belege im selben Monatsbild.
    if (filter === 'bank_review') return true;
    if (filter === 'paid') return false;
    return !isResolvedMonth(row) && row.derived_state === filter;
  
});
  const filteredSpecialPayments = overview?.specialPayments || [];
  const filteredHistoricalRentPayments = overview?.historicalRentPayments || [];
  const filteredUnassignedObjectCandidates = overview?.unassignedObjectCandidates || [];
  const filteredUnassignedPaymentCandidates = overview?.unassignedPaymentCandidates || [];

  // Nur die ausdrücklich aktivierten BMF-, Neckarauer- und Waldhof-NK-Forderungen
  // werden rein visuell nach ihrer regulären Monatszeile derselben property_id +
  // unit_id eingeordnet. Empfänger, Kennzahlen, Status, Beträge, Filter und
  // Zahlungslogik werden nicht verändert.
  const displayRows = (() => {
    const isInlineSettlementClaim = (row) => row?.is_settlement_claim === true
      && /^(bmf|nas|whs)_settlement_claim_/.test(String(row.id || ''));
    const inlineClaims = filteredRows.filter(isInlineSettlementClaim);
    if (inlineClaims.length === 0) return filteredRows;

    const claimsByUnit = new Map();
    for (const claim of inlineClaims) {
      const rowKey = `${String(claim.property_id || '')}:${String(claim.unit_id || '')}`;
      if (!claimsByUnit.has(rowKey)) claimsByUnit.set(rowKey, []);
      claimsByUnit.get(rowKey).push(claim);
    }

    const placedClaims = new Set();
    const orderedRows = [];
    for (const row of filteredRows) {
      if (isInlineSettlementClaim(row)) continue;
      orderedRows.push(row);
      const rowKey = `${String(row.property_id || '')}:${String(row.unit_id || '')}`;
      const unitClaims = claimsByUnit.get(rowKey) || [];
      for (const claim of unitClaims) {
        orderedRows.push(claim);
        placedClaims.add(claim);
      }
    }

    // Eine aktivierte Claimzeile ohne passende reguläre Einheit bleibt sichtbar
    // statt stillschweigend unterdrückt zu werden; dies ist nur Positionslogik.
    return orderedRows.concat(inlineClaims.filter((claim) => !placedClaims.has(claim)));
  })();
  
  useEffect(() => {
    if (tenantDetail || !overview?.rows?.length) return;
    const params = new URLSearchParams(window.location.search);
    const tenantName = params.get('tenant');
    const propertyId = params.get('tenant_property');
    const unitId = params.get('tenant_unit');
    const requestedYear = Number(params.get('rent_year'));
    if (!tenantName || !propertyId || !unitId || !requestedYear) return;
    if (requestedYear !== currentYear) {
      setCurrentYear(requestedYear);
      return;
    }
    const matchingPayment = overview.rows.find(row => String(row.property_id) === propertyId && String(row.unit_id) === unitId && row.tenant_name === tenantName);
    if (matchingPayment) handleTenantLedger(matchingPayment);
  }, [overview, currentYear, tenantDetail]);

  // ============================================================
  // RENDER
  // ============================================================
  
  if (loading && !overview) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Lade Daten...</p>
        </div>
      </div>
    );
  }
  
  const unitProfileModal = unitProfile ? (
    <PaymentTrafficModal
      unit={unitProfile.unit}
      months={unitProfile.months || []}
      ampel={unitProfile.ampel}
      year={unitProfile.year}
      credits={unitProfile.credits || []}
      loading={unitProfileLoading}
      error={unitProfileError}
      onClose={() => setUnitProfile(null)}
      onOpenPdf={handleUnitProfilePdf}
      onDun={() => handleDunFromTraffic(unitProfile?.payment || unitProfile?.unit)}
    />
  ) : null;
  if (tenantDetail) {
    const activeLedger = tenantLedgers[tenantDetail.key];
    return <div className="p-4 md:p-6">{unitProfileModal}<TenantMonthlyReviewDetailView ledger={activeLedger} payment={tenantDetail.payment} loading={ledgerLoadingKey === tenantDetail.key} hasError={ledgerErrorKey === tenantDetail.key} onBack={closeTenantDetail} onRetry={() => handleTenantLedger(tenantDetail.payment, tenantDetail.year, true)} onSelectYear={(nextYear) => handleTenantLedger(tenantDetail.payment, nextYear)} onConfirmRentBasis={handleTenantLedgerRentBasisConfirmation} onConfirmPeriod={handleTenantLedgerPeriodConfirmation} onOpenArrearsCommunication={handleOpenArrearsCommunication} onOpenPaymentTraffic={() => handleUnitProfile(tenantDetail?.payment)} />
      {showCommModal && commPayment && <RentPaymentCommunicationModal
        channel={commChannel}
        paymentData={commPayment}
        tenantData={{ id: commPayment.tenant_id, name: commPayment.tenant_name, email: commPayment.tenant_email, phone: commPayment.tenant_phone }}
        propertyData={{ id: commPayment.property_id, property_name: commPayment.property_name, street: commPayment.property_address, city: commPayment.property_city }}
        ownerData={{ name: 'Verwaltung', phone: '', email: '' }}
                arrearsSnapshot={commArrearsSnapshot}
        communicationPurpose={commPurpose || 'arrears'}
        onClose={() => {
          setShowCommModal(false);
          setCommPayment(null);
          setCommArrearsSnapshot(null);
          setCommPurpose(null);
          setCommChannel(null);
        }}
        testMode={Boolean(commPayment.is_test_data)}
        onTestDryRun={handleTestCommunicationDryRun}
        onTestWhatsAppLocalValidate={handleTestWhatsAppLocalValidate}
        onTestEmailReview={handleTestEmailReview}
        onTestEmailSend={handleTestEmailSend}
        onProductionContactSave={handleProductionContactSave}
        onProductionEmailReview={handleProductionEmailReview}
        onProductionEmailSend={handleProductionEmailSend}
        onSent={() => { loadMonthData(); }}
      />}
    </div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* === Auswahlfenster Zahlungen zuordnen === */}
      {zaOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Zahlungen zuordnen">
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Zahlungen zuordnen</h3>
            <p className="mt-1 text-xs text-slate-500">Reine Zuordnung – keine Buchung, keine Verrechnung. Nur mit bewusstem Klick.</p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Objekt</label>
                <select value={zaObjekt} onChange={(e) => setZaObjekt(e.target.value)} className="mt-1 w-full rounded border border-slate-300 p-2 text-sm dark:border-slate-600 dark:bg-slate-800">
                  <option value="">– Objekt wählen –</option>
                  {(properties || []).map((p) => <option key={p.id || p.property_id} value={p.id || p.property_id}>{p.name || p.property_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Zeitraum</label>
                <div className="mt-1 flex items-center gap-2">
                  <select value={zaJahr} onChange={(e) => setZaJahr(Number(e.target.value))} className="rounded border border-slate-300 p-2 text-sm dark:border-slate-600 dark:bg-slate-800">
                    {[2024, 2025, 2026].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select value={zaMonat} disabled={zaGanzesJahr} onChange={(e) => setZaMonat(Number(e.target.value))} className="rounded border border-slate-300 p-2 text-sm disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                  </select>
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={zaGanzesJahr} onChange={(e) => setZaGanzesJahr(e.target.checked)} />ganzes Jahr</label>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Mieter</label>
                <label className="mt-1 flex items-center gap-2 text-sm"><input type="checkbox" checked={zaAlle} onChange={(e) => setZaAlle(e.target.checked)} />Alle Mieter</label>
                <select multiple disabled={zaAlle} value={zaMieter} onChange={(e) => setZaMieter(Array.from(e.target.selectedOptions).map((o) => o.value))} className="mt-1 w-full rounded border border-slate-300 p-2 text-sm disabled:opacity-40 dark:border-slate-600 dark:bg-slate-800" size={4}>
                  {zaAlleMieter.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                {zaAlle && <p className="text-[10px] text-slate-400">Einzelne Mieter sind ausgegraut, solange „Alle Mieter" angekreuzt ist.</p>}
              </div>
              <div className="rounded bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                Vorschau: {zaObjekt ? (properties || []).find((p) => (p.id || p.property_id) === zaObjekt)?.name || 'Objekt' : '–'} · {zaGanzesJahr ? `Jan–Dez ${zaJahr}` : `${String(zaMonat).padStart(2, '0')}.${zaJahr}`} · {zaAlle ? 'alle Mieter' : `${zaMieter.length} Mieter`}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button type="button" onClick={() => setZaOpen(false)} className="rounded px-3 py-2 text-sm text-slate-500 hover:bg-slate-100">Schließen</button>
              <button type="button" onClick={handleZaStart} disabled={zaBusy || !zaObjekt} className="rounded bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
                {zaBusy ? (<span className="flex items-center gap-2"><span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />Ordnet zu...</span>) : 'Jetzt zuordnen'}
              </button>
            </div>
            {zaStatus && <div className="mt-3 text-xs text-emerald-600">{zaStatus}</div>}
            {zaFehler && <div className="mt-3 text-xs text-red-600">{zaFehler}</div>}
            {zaReport && !zaFehler && (
              <div className="mt-3">
                <button type="button" onClick={zaDownload} className="rounded bg-slate-800 px-3 py-2 text-xs text-white hover:bg-slate-700">Report herunterladen</button>
              </div>
            )}
          </div>
        </div>
      )}
      {unitProfileModal}
      <NkMahnNotice mode="inline" />
      {/* ============================================================ */}
      {/* HEADER */}
      {/* ============================================================ */}
      
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          🏦 Mieteingangskontrolle
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Übersicht und Bestätigung der monatlichen Mieteingänge
        </p>
      </div>
      
      {/* ============================================================ */}
      {/* OWNER/PROPERTY FILTER */}
      {/* ============================================================ */}
      
      <div className="flex items-center gap-4 mb-6">
        {/* Owner Dropdown */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Eigentümer
          </label>
          <select
            value={selectedOwner}
            onChange={(e) => setSelectedOwner(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          >
            <option value="all">Alle Eigentümer</option>
            {owners.map(owner => (
              <option key={owner.id} value={owner.id}>
                {owner.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Property Dropdown */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Objekt
          </label>
          <select
            value={selectedProperty}
            onChange={(e) => setSelectedProperty(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
          >
            <option value="all">Alle Objekte</option>
            {properties
              .filter(property => selectedOwner === 'all' || property.owner_entity === selectedOwner)
              .map(property => (
                <option key={property.id} value={property.id}>
                  {property.property_name}
                </option>
              ))}
          </select>
        </div>

        {/* Mieter Dropdown (15.10.2026) */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Mieter
          </label>
          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">Alle Mieter</option>
            {[...new Set((overview?.rows || []).filter((row) => {
              if (selectedOwner !== 'all') {
                if (properties.length === 0) return false;
                const property = properties.find(p => p.id === row.property_id);
                if (!property || property.owner_entity !== selectedOwner) return false;
              }
              return selectedProperty === 'all' || row.property_id === selectedProperty;
            }).map(row => String(row.tenant_name || '')).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'de')).map(name => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
      </div>

      
      {/* ============================================================ */}
      {/* MONTH NAVIGATION */}
      {/* ============================================================ */}
      
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={goToPreviousMonth}
            variant="outline"
            size="sm"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="text-xl font-semibold text-gray-900 dark:text-white min-w-[150px] text-center">
            {getMonthName(currentMonth)} {currentYear}
          </div>
          
          <Button
            onClick={goToNextMonth}
            variant="outline"
            size="sm"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          
          <Button
            onClick={goToCurrentMonth}
            variant="outline"
            size="sm"
          >
            Heute
          </Button>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setShowCsvUpload(true)}
            variant="outline"
            size="sm"
          >
            <Upload className="w-4 h-4 mr-2" />
            CSV Upload
          </Button>
          
          <Button
            onClick={() => setShowSmartReview(!showSmartReview)}
            variant={showSmartReview ? "default" : "outline"}
            size="sm"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {showSmartReview ? "Zurück zur Übersicht" : "Smart Review"}
          </Button>
          

          <Button
            onClick={handleBootstrap}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Bootstrap
          </Button>
            <button type="button" onClick={() => setZaOpen(true)} className="flex items-center gap-1 rounded-lg border border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-900/30 dark:text-violet-300">Zahlungen zuordnen</button>
          
          <Button
            onClick={handleSync}
            variant="outline"
            size="sm"
            title="Sollbeträge aus Stammdaten aktualisieren"
          >
            <ArrowUpDown className="w-4 h-4" />
          </Button>
        </div>
      </div>
      

      {/* ============================================================ */}
      {/* CONDITIONAL RENDERING: Smart Review or Normal View */}
      {/* ============================================================ */}
      
      {showSmartReview ? (
        <div className="mt-6">
          <TransactionReviewQueue />
        </div>
      ) : (
        <>
          {/* ============================================================ */}
                {/* SUMMARY CARDS */}
      {/* ============================================================ */}
      {overview?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Soll-Einnahmen */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Soll-Einnahmen</span>
              <DollarSign className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(overview.summary.expected)}
            </div>
            {overview.summary.rent_component_complete_count > 0 && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Nettomiete {formatCurrency(overview.summary.cold_rent_total)} + NK {formatCurrency(overview.summary.utilities_advance_total)} · {overview.summary.rent_component_complete_count}/{overview.summary.total_count} vollständig aufgeteilt</div>}
          </div>
          
          {/* Ist-Einnahmen */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Ist-Einnahmen</span>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(overview.summary.received)}
            </div>
            {(overview.summary.nk_credit_total || 0) > 0 && (
              <div className="text-xs text-emerald-700 mt-1">
                zzgl. bestätigtes NK-Guthaben: {formatCurrency(overview.summary.nk_credit_total)}
              </div>
            )}
          </div>
          
          {/* Abweichung */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Abweichung</span>
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <div className={`text-2xl font-bold ${overview.summary.open > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(overview.summary.open)}
            </div>
          </div>
          
          {/* Quote */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Eingangsquote</span>
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {(overview.summary.quote * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {overview.summary.paid_count} / {overview.summary.total_count} bezahlt
            </div>
          </div>
          {/* Sonderzahlungen Karte */}
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">Sonderzahlungen</span>
              <Euro className="w-5 h-5 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {formatCurrency(overview.summary.special_payment_total || 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {overview.summary.special_payment_count || 0} Buchungen
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================================ */}
      {/* FILTER TABS */}
      {/* ============================================================ */}
      
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { value: 'all', label: 'Alle', count: overview?.rows?.length || 0 },
          { value: 'open', label: 'Offen', count: overview?.summary?.open_count || 0 },
          { value: 'partial', label: 'Teilzahlung', count: overview?.summary?.partial_count || 0 },
          { value: 'overdue', label: 'Überfällig', count: overview?.summary?.overdue_count || 0 },
          { value: 'bank_review', label: 'Bankbelege prüfen', count: (overview?.summary?.unassigned_object_candidate_count || 0) + (overview?.summary?.unassigned_payment_candidate_count || 0) + (overview?.summary?.tenant_review_bank_receipt_count || 0) },
          { value: 'vacant', label: 'Nicht bewohnt', count: overview?.summary?.vacant_unit_count || 0 },
          { value: 'special', label: 'Sonderzahlungen', count: overview?.summary?.special_payment_count || 0 },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => setFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === tab.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-900 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>
      
      {/* ============================================================ */}
      {/* PAYMENTS TABLE */}
      {/* ============================================================ */}
      
      {filter === 'bank_review' && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-100">
          <div className="font-semibold">Mieten und Bankbelege gemeinsam prüfen</div>
          <div className="mt-1 text-xs">Die vollständigen Sollmieten dieses Monats bleiben sichtbar. Offene CAMT-Belege folgen darunter zur getrennten Bestätigung von Objekt, Mieter, Zielmonat und Zahlungsstatus.</div>
        </div>
      )}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mieter
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Objekt
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Soll
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ist
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Offen
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fällig
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    Keine Zahlungen gefunden
                  </td>
                </tr>
              ) : (
                displayRows.map(payment => (
                  <React.Fragment key={`tenant_row_${payment.id}`}>
                  <tr className={payment.is_vacant ? 'bg-slate-50 dark:bg-slate-800/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}>
                    {/* Belegprüfung: genau ein klarer Ampel-Button pro Mietmonat. */}
                    <td className="px-4 py-3">
                      {payment.is_vacant ? (
                        <span className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-100">Nicht bewohnt</span>
                      ) : payment.is_settlement_claim ? (
                        <span className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"><Clock className="h-4 w-4" />NK-Abrechnung offen</span>
                      ) : (() => {
                        const reviewButton = getPaymentReviewButton(payment);
                        return (
                          <button
                            type="button"
                            onClick={() => handleTenantLedger(payment)}
                            title={reviewButton.detail}
                            aria-label={`${reviewButton.label}: ${reviewButton.detail}`}
                            className={`inline-flex max-w-[220px] items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:ring-offset-slate-900 ${reviewButton.tone}`}
                          >
                            {reviewButton.icon}
                            <span className="truncate">{reviewButton.label}</span>
                          </button>
                        );
                      })()}
                    </td>
                    
                    {/* Mieter */}
                    <td className="px-4 py-3">
                      {payment.is_vacant ? <div className="text-left"><span className="block text-sm font-semibold text-slate-700 dark:text-slate-200">Nicht bewohnt</span><span className="block text-xs text-gray-500">Einheit {payment.unit_number}</span></div> : payment.is_settlement_claim ? <button type="button" onClick={() => handleUnitProfile(payment)} className="group inline-flex items-start gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"><span><span className="block text-sm font-medium text-gray-900 group-hover:text-violet-700 dark:text-white dark:group-hover:text-violet-300">{payment.tenant_name}</span>{payment.unit_number && <span className="block text-xs text-gray-500">Einheit {payment.unit_number}</span>}<span className="mt-1 block text-[11px] font-medium text-violet-600 group-hover:text-violet-800 dark:text-violet-300">Einheitendaten öffnen</span></span></button> : <button type="button" onClick={() => handleTenantLedger(payment)} className="group inline-flex items-start gap-2 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500">
                        <span><span className="block text-sm font-medium text-gray-900 group-hover:text-violet-700 dark:text-white dark:group-hover:text-violet-300">{payment.tenant_name}</span>{payment.unit_number && <span className="block text-xs text-gray-500">Einheit {payment.unit_number}</span>}{payment.is_test_data && <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">TESTDATEN</span>}<span className="mt-1 block text-[11px] font-medium text-violet-600 group-hover:text-violet-800 dark:text-violet-300 dark:group-hover:text-violet-100">Mietverlauf öffnen</span></span>
                        <ChevronRight className="mt-1 h-4 w-4 text-gray-400 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-600 dark:group-hover:text-violet-300" />
                      </button>}
                    </td>
                    
                    {/* Objekt */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {payment.property_name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {payment.property_address}
                      </div>
                    </td>
                    
                    {/* Soll */}
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {payment.is_vacant ? '—' : formatCurrency(payment.expected_amount)}
                      </div>
                      {payment.is_vacant && <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-300">Keine laufende Sollmiete</div>}
                      {(payment.is_settlement_letter_review || payment.is_settlement_claim) && <div className="mt-1 text-[11px] font-medium text-violet-700 dark:text-violet-300">{payment.review_label}</div>}
                      {rentComponentLabel(payment.cold_rent_amount, payment.utilities_advance_amount, payment.warm_rent_amount ?? payment.expected_amount) && <div className="mt-1 max-w-[280px] text-[11px] leading-4 text-gray-500 dark:text-gray-400">{rentComponentLabel(payment.cold_rent_amount, payment.utilities_advance_amount, payment.warm_rent_amount ?? payment.expected_amount)}</div>}
                      {payment.rent_component_status === 'nk_component_not_separately_evidenced' && <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">NK-Komponente nicht separat belegt</div>}
                      {payment.rent_component_status === 'component_total_review_required' && <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">Mietkomponenten prüfen</div>}
                      {(() => {
                        const comparison = payment.is_vacant ? null : rentWorkingComparisonPresentation(payment);
                        return comparison ? <div className={`mt-1 text-[11px] font-medium ${comparison.tone}`} title={comparison.note}>{comparison.label}</div> : null;
                      })()}
                      {(() => {
                        const hint = inlineLedgerHints[inlineLedgerHintKey(payment, currentYear, currentMonth)];
                        if (!hint || payment.is_vacant) return null;
                        const tone = hint.status === 'proposal_available' ? 'text-sky-700 dark:text-sky-300' : 'text-slate-600 dark:text-slate-400';
                        return <div className={`mt-1 max-w-[280px] text-[11px] font-medium leading-4 ${tone}`} title={hint.note}>{hint.label}</div>;
                      })()}
                    </td>
                    
                    {/* Ist */}
                    <td className="px-4 py-3 text-right">
                      <div className={`text-sm font-medium ${
                        ['paid', 'nk_credit_deduction'].includes(payment.derived_state)
                          ? 'text-green-600'
                          : payment.received_amount > 0
                          ? 'text-yellow-600'
                          : 'text-gray-400'
                      }`}>
                        {payment.is_vacant || payment.is_settlement_letter_review ? '—' : formatCurrency(payment.received_amount)}
                      </div>
                      {Number(payment.tenant_review_bank_receipt_count || 0) > 0 && <div className="mt-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">{payment.tenant_review_bank_receipt_count} CAMT-Prüfbeleg{Number(payment.tenant_review_bank_receipt_count) === 1 ? '' : 'e'} · nicht als Ist</div>}
                      {parseFloat(payment.nk_credit_amount || 0) > 0 && (
                        <div className="text-xs text-emerald-700 mt-1 whitespace-nowrap">
                          abzüglich NK-Guthaben: {formatCurrency(payment.nk_credit_amount)}
                        </div>
                      )}
                    </td>
                    
                    {/* Offen */}
                    <td className="px-4 py-3 text-right">
                      <div className={`text-sm font-medium ${payment.control_review_required ? 'text-amber-700 dark:text-amber-300' : 'text-red-600'}`}>
                        {payment.is_vacant || payment.is_settlement_letter_review ? '—' : payment.control_review_required ? '—' : formatCurrency(payment.outstanding_amount)}
                      </div>
                      {payment.control_review_required && !payment.is_vacant && !payment.is_settlement_letter_review && <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">Kein forderungsfähiger Saldo</div>}
                    </td>
                    
                    {/* Fällig */}
                    <td className="px-4 py-3 text-center">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {payment.is_vacant ? '—' : new Date(payment.due_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      </div>
                    </td>
                    
                    {/* Aktionen */}
                    <td className="px-4 py-3 text-right">
                      {payment.is_vacant ? <span className="text-xs text-gray-400" title="Keine Miete, Buchung, Mahnung oder Kommunikation für leerstehende Wohnungen">—</span> : filter === 'bank_review' ? (
                        (payment.is_settlement_letter_review || payment.is_settlement_claim) ? <span className="text-xs text-gray-400">—</span> : <Button onClick={() => handleTenantLedger(payment)} size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-200" title="CAMT-Beleg und Mietverlauf prüfen"><FileText className="mr-1 h-4 w-4" />Beleg prüfen</Button>
                      ) : <div className="flex justify-end gap-2">
                        {(payment.is_settlement_letter_review || payment.is_settlement_claim) ? null : payment.control_review_required ? (
                          <Button onClick={() => handleOpenPaymentReconciliationEmail(payment)} size="sm" variant="outline" className="border-yellow-300 bg-white text-yellow-700 hover:bg-yellow-50 dark:border-yellow-800 dark:bg-slate-900 dark:text-yellow-200" title="Neutralen Zahlungsabgleich per E-Mail vorbereiten">E-Mail schreiben</Button>
                        ) : !payment.quick_actions_blocked && !['paid', 'nk_credit_deduction'].includes(payment.derived_state) && (
                          <>
                            <Button onClick={() => { setCommPayment(payment); setCommChannel('whatsapp'); setShowCommModal(true); }} size="sm" variant="outline" className="text-green-600 hover:bg-green-50" title={payment.is_test_data ? 'WhatsApp-Vorschau (Test-Dry-Run)' : 'WhatsApp senden'}><MessageSquare className="w-4 h-4" /></Button>
                            <Button onClick={() => { setCommPayment(payment); setCommChannel('email'); setShowCommModal(true); }} size="sm" variant="outline" className="text-blue-600 hover:bg-blue-50" title={payment.is_test_data ? 'E-Mail-Vorschau (Test-Dry-Run)' : 'E-Mail senden'}><Mail className="w-4 h-4" /></Button>
                            <Button onClick={() => handleQuickConfirm(payment)} size="sm" variant="outline" className="text-green-600 hover:bg-green-50" title="Bezahlt markieren"><CheckCircle className="w-4 h-4" /></Button>
                            <Button onClick={() => handlePartialPayment(payment)} size="sm" variant="outline" className="text-yellow-600 hover:bg-yellow-50" title="Teilzahlung"><Euro className="w-4 h-4" /></Button>
                          </>
                        )}
                        {['paid', 'nk_credit_deduction'].includes(payment.derived_state) && (
                          <Button onClick={() => handleReopen(payment)} size="sm" variant="outline" className="text-gray-600 hover:bg-gray-50" title="Wiedereröffnen"><XCircle className="w-4 h-4" /></Button>
                        )}
                      </div>}
                    </td>
                  </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(filter === 'all' || filter === 'bank_review') && filteredUnassignedObjectCandidates.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-lg border-l-4 border-blue-500 bg-white shadow dark:bg-slate-900">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <FileText className="h-5 w-5 text-blue-600" />
              Bankbelege: Objekt bestätigen
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              CAMT-Gutschriften aus einem gemeinsamen Bankkonto. Ein angezeigter Objektvorschlag ist nur eine Prüfhilfe und noch keine Zuordnung. Zuerst wird ausschließlich das Objekt menschlich bestätigt.
            </p>
            <p className="mt-1 text-xs font-medium text-blue-800 dark:text-blue-200">
              Danach folgen getrennt Mieter und Einheit, Zielmonat sowie Zahlungsstatus. Vorher entsteht weder Miet-Ist noch Plusbetrag, Saldo, Buchung, Mahnung oder Kommunikation.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-blue-50 dark:bg-blue-950/20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Zahler</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Betrag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Verwendungszweck / Prüfhilfe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Objekt bestätigen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUnassignedObjectCandidates.map(candidate => (
                  <tr key={candidate.id} className="align-top hover:bg-blue-50/60 dark:hover:bg-blue-950/20">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 rounded-lg bg-blue-100 px-3 py-2 text-xs font-semibold text-blue-900 dark:bg-blue-900/30 dark:text-blue-100">
                        <AlertCircle className="h-4 w-4" />{candidate.suggested_property_id ? 'Objektvorschlag' : 'Objekt offen'}
                      </span>
                      <div className="mt-1 max-w-[220px] text-[11px] leading-4 text-blue-800 dark:text-blue-200">{candidate.review_hint}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {candidate.booking_date ? new Date(candidate.booking_date).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{candidate.counterparty_name || 'nicht überliefert'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-blue-700 dark:text-blue-300">{formatCurrency(candidate.amount)}</td>
                    <td className="px-4 py-3">
                      <div className="max-w-xl break-words text-sm text-gray-700 dark:text-gray-200">{candidate.reference || 'Kein Verwendungszweck überliefert'}</div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">CAMT-Nachweis {candidate.statement_evidence ? 'vorhanden' : 'nicht bestätigt'} · {candidate.suggested_property_id ? `Vorschlag ${candidate.suggested_property_name || candidate.suggested_property_id}, noch nicht bestätigt` : 'keine Objektannahme'}</div>
                    </td>
                    <td className="min-w-[280px] px-4 py-3">
                      <select
                        value={objectAssignmentSelections[candidate.id] || candidate.suggested_property_id || ''}
                        onChange={(event) => setObjectAssignmentSelections((previous) => ({ ...previous, [candidate.id]: event.target.value }))}
                        className="block w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-blue-800 dark:bg-slate-950 dark:text-white"
                      >
                        <option value="">Objekt auswählen …</option>
                        {(candidate.object_assignment_options || []).map((option) => (
                          <option key={`${candidate.id}-${option.property_id}`} value={option.property_id}>{option.property_name}</option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!(objectAssignmentSelections[candidate.id] || candidate.suggested_property_id) || assigningObjectCandidateId === candidate.id || !candidate.statement_evidence}
                        onClick={() => handleBankObjectAssignment(candidate)}
                        className="mt-2 w-full bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
                      >
                        {assigningObjectCandidateId === candidate.id ? 'Objekt wird gespeichert …' : 'Objekt bestätigen'}
                      </Button>
                      <div className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">Mieter, Einheit, Zielmonat und Zahlungsstatus bleiben danach offen.</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(filter === 'all' || filter === 'bank_review') && filteredUnassignedPaymentCandidates.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-lg border-l-4 border-amber-500 bg-white shadow dark:bg-slate-900">
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
              <FileText className="h-5 w-5 text-amber-600" />
              Bankbelege prüfen
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Vorhandene CAMT-Gutschriften mit feststehendem Objekt. Jetzt werden ausschließlich Mieter und Einheit menschlich bestätigt; anschließend folgen im Tenant-Ledger Zielmonat und Zahlungsstatus getrennt.
            </p>
            <p className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-200">
              Die Mieterzuordnung allein zählt nicht als Miet-Ist, Plusbetrag oder Saldo und löst keine Buchung, Mahnung oder Kommunikation aus.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-amber-50 dark:bg-amber-950/20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Zahler</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Betrag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Verwendungszweck / Prüfhilfe</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Mieter zuordnen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUnassignedPaymentCandidates.slice().sort((a, b) => ({ high: 0, medium: 1, low: 2 }[a.trust_class] ?? 3) - ({ high: 0, medium: 1, low: 2 }[b.trust_class] ?? 3)).map(candidate => (
                  <tr key={candidate.id} className="align-top hover:bg-amber-50/60 dark:hover:bg-amber-950/20">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-2 rounded-lg bg-amber-100 px-3 py-2 text-xs font-semibold text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
                        <AlertCircle className="h-4 w-4" />Objekt fest · Mieter offen
                      </span>
                      <div className="mt-1"><span className={`inline-flex rounded-lg px-2 py-1 text-[11px] font-semibold ${candidate.trust_class === 'high' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : candidate.trust_class === 'medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'}`}>{candidate.trust_label_short || 'Unklar'}</span></div>
                      <details className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400"><summary className="cursor-pointer font-semibold">Details &amp; Evidence</summary><div className="mt-1 space-y-0.5"><div>{candidate.trust_label || 'Keine Einschätzung hinterlegt.'}</div><div>Clara-Confidence: {candidate.trust_confidence != null ? `${Math.round(Number(candidate.trust_confidence) * 100)} %` : '—'}</div>{(candidate.trust_signals || []).map((signal) => (<div key={signal}>· {signal}</div>))}</div></details>
                      <div className="mt-1 max-w-[220px] text-[11px] leading-4 text-amber-800 dark:text-amber-200">{candidate.review_hint}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {candidate.booking_date ? new Date(candidate.booking_date).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {candidate.counterparty_name || 'nicht überliefert'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-semibold text-amber-700 dark:text-amber-300">
                      {formatCurrency(candidate.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-xl break-words text-sm text-gray-700 dark:text-gray-200">{candidate.reference || 'Kein Verwendungszweck überliefert'}</div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">CAMT-Nachweis {candidate.statement_evidence ? 'vorhanden' : 'nicht bestätigt'} · Objekt-Scope festgelegt</div>
                    </td>
                    <td className="min-w-[280px] px-4 py-3">
                      <select
                        value={bankAssignmentSelections[candidate.id] || ''}
                        onChange={(event) => setBankAssignmentSelections((previous) => ({ ...previous, [candidate.id]: event.target.value }))}
                        className="block w-full rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-amber-800 dark:bg-slate-950 dark:text-white"
                      >
                        <option value="">Mieter / Einheit auswählen …</option>
                        {(candidate.assignment_options || []).map((option) => (
                          <option key={`${candidate.id}-${option.unit_id}`} value={option.unit_id}>{option.unit_identifier} · {option.tenant_name}</option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!bankAssignmentSelections[candidate.id] || assigningBankCandidateId === candidate.id || !candidate.statement_evidence}
                        onClick={() => handleBankTenantAssignment(candidate)}
                        className="mt-2 w-full bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50"
                      >
                        {assigningBankCandidateId === candidate.id ? 'Zuordnung wird gespeichert …' : 'Mieter zuordnen'}
                      </Button>
                      <div className="mt-1 text-[11px] leading-4 text-gray-500 dark:text-gray-400">Danach bleiben Zielmonat und Zahlungsstatus im Tenant-Ledger offen.</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* SONDERZAHLUNGEN TABELLE */}
      {/* ============================================================ */}
      {filter === 'all' && filteredHistoricalRentPayments.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow overflow-hidden mt-6 border-l-4 border-indigo-500">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-indigo-600" />
              Historische Mieten
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {filteredHistoricalRentPayments.length} Buchungen · {formatCurrency(filteredHistoricalRentPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0))} Gesamtvolumen · historisch zugeordnet
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zahler</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zuordnung / Befund</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Objekt</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verwendungszweck</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredHistoricalRentPayments.map(payment => (
                  <tr key={payment.id} className="hover:bg-indigo-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {payment.booking_date ? new Date(payment.booking_date).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{payment.counterparty_name}</td>
                    <td className="px-4 py-3 max-w-sm">
                      <div className="text-sm text-indigo-700 dark:text-indigo-400">{payment.matched_entity || 'Historischer Mieter'}</div>
                      <div className="mt-1 text-xs font-medium text-indigo-600 dark:text-indigo-300">{payment.historical_rent_label}</div>
                      {payment.unit_allocation ? (
                        <div className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">Einheit {payment.unit_allocation} bestätigt · historische Mietforderung</div>
                      ) : (
                        <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">Objektzuordnung bestätigt · Einheit historisch nicht belegt</div>
                      )}
                      {payment.allocation_finding && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{payment.allocation_finding}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{payment.property_name || '—'}</td>
                    <td className="px-4 py-3 text-right"><span className="text-sm font-semibold text-indigo-600">{formatCurrency(payment.amount)}</span></td>
                    <td className="px-4 py-3"><div className="text-xs text-gray-500 max-w-xs truncate" title={payment.reference}>{payment.reference || '—'}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(filter === 'all' || filter === 'special') && filteredSpecialPayments.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow overflow-hidden mt-6">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Euro className="w-5 h-5 text-purple-600" />
              Sonderzahlungen
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {filteredSpecialPayments.length} Buchungen · {formatCurrency(filteredSpecialPayments.reduce((s, p) => s + parseFloat(p.amount || 0), 0))} Gesamtvolumen
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zahler</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zuordnung / Befund</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Objekt</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Betrag</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verwendungszweck</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSpecialPayments.map(sp => (
                  <tr key={sp.id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {sp.booking_date ? new Date(sp.booking_date).toLocaleDateString('de-DE') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{sp.counterparty_name}</div>
                    </td>
                    <td className="px-4 py-3 max-w-sm">
                      <div className="text-sm text-purple-700 dark:text-purple-400">
                        {sp.matched_entity || <span className="text-gray-400 italic">Kein Mieter zugeordnet</span>}
                      </div>
                      {sp.special_payment_label && (
                        <div className="mt-1 text-xs font-medium text-purple-600 dark:text-purple-300">
                          {sp.special_payment_label}
                        </div>
                      )}
                      {sp.allocation_scope === 'property_level' && (
                        <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                          Objektzuordnung bestätigt · Einheit nicht bestätigt
                        </div>
                      )}
                      {sp.allocation_finding && (
                        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                          {sp.allocation_finding}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {sp.property_id ? sp.property_id.replace(/_/g,' ') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-purple-600">{formatCurrency(sp.amount)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-500 max-w-xs truncate" title={sp.reference}>
                        {sp.reference || '—'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

        </>
      )}
      
      {/* ============================================================ */}
      {/* CSV UPLOAD MODAL */}
      {/* ============================================================ */}
      
      {showCsvUpload && (
        <RentCsvUpload
          onDone={(batchId) => {
            console.log('✅ CSV Upload completed:', batchId);
            setCurrentBatchId(batchId);
            setShowCsvUpload(false);
            setShowMatchReview(true);
          }}
          onClose={() => setShowCsvUpload(false)}
        />
      )}
      
      {showMatchReview && currentBatchId && (
        <CsvMatchReview
          batchId={currentBatchId}
          onComplete={() => {
            loadMonthData(); // Reload data after confirming matches
          }}
          onClose={() => {
            setShowMatchReview(false);
            setCurrentBatchId(null);
          }}
        />
      )}
      
      {/* Communication Modal */}
      {showCommModal && commPayment && (
        <RentPaymentCommunicationModal
          channel={commChannel}
          paymentData={commPayment}
          tenantData={{
            id: commPayment.tenant_id,
            name: commPayment.tenant_name,
            email: commPayment.tenant_email,
            phone: commPayment.tenant_phone
          }}
          propertyData={{
            id: commPayment.property_id,
            property_name: commPayment.property_name,
            street: commPayment.property_address,
            city: commPayment.property_city
          }}
          ownerData={{
            name: 'Verwaltung', // TODO: Load from owners table
            phone: '',
            email: ''
          }}
          arrearsSnapshot={commArrearsSnapshot}
          communicationPurpose={commPurpose || 'arrears'}
          onClose={() => {
            setShowCommModal(false);
            setCommPayment(null);
            setCommArrearsSnapshot(null);
            setCommPurpose(null);
            setCommChannel(null);
          }}
          testMode={Boolean(commPayment.is_test_data)}
          onTestDryRun={handleTestCommunicationDryRun}
          onTestEmailReview={handleTestEmailReview}
          onTestEmailSend={handleTestEmailSend}
          onProductionContactSave={handleProductionContactSave}
          onProductionEmailReview={handleProductionEmailReview}
          onProductionEmailSend={handleProductionEmailSend}
          onSent={() => {
            loadMonthData(); // Reload to show communication history
          }}
        />
      )}
    </div>
  );
};

export default RentTrackingPage;
// Miete + Kaution (16.10.): Aufteilungs-Vorschlag aus Banktext, sonst Soll-Abzug.
// Nur Vorschlag - die Bestaetigung bleibt immer ein bewusster Buttonklick.
const proposeMieteKautionSplit = (entry, expectedEur) => {
  const reference = String(entry?.reference_excerpt || entry?.reference_raw || '').replace(/\s+/g, ' ');
  const total = Number(entry?.amount_eur || 0);
  const expected = Number(expectedEur || 0);
  const parseDe = (raw) => Number(String(raw).replace(/\./g, '').replace(',', '.'));
  const kautionMatch = reference.match(/kaution\s*([0-9][0-9.,]*)/i);
  const mieteMatch = reference.match(/miete\s*([0-9][0-9.,]*)/i);
  if (kautionMatch && mieteMatch) {
    const kaution = parseDe(kautionMatch[1]);
    const miete = parseDe(mieteMatch[1]);
    if (miete > 0 && kaution > 0) return { miete: Number(miete.toFixed(2)), kaution: Number(kaution.toFixed(2)) };
  }
  if (expected > 0 && total > expected) {
    return { miete: Number(expected.toFixed(2)), kaution: Number((total - expected).toFixed(2)) };
  }
  return { miete: 0, kaution: Number(total.toFixed(2)) };
};
